import { useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Textarea } from '../ui/textarea';
import { Search, Plus, Eye, BarChart3, Bot, ExternalLink, Loader2, AlertCircle, Settings, CheckCircle, XCircle, Wrench, Monitor, Volume2, Mic, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, MoreHorizontal, Phone, CreditCard, Shield, PauseCircle, PlayCircle, ArrowRight, Download, Trash2, Sparkles, DollarSign, HelpCircle } from 'lucide-react';
import { CustomerCostOverviewDialog } from '../customer/CustomerCostOverviewDialog';
import { Progress } from '../ui/progress';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { apiService, Customer, CustomerModelAccess, LLMModel, GrantModelAccessRequest, ToolDefinition, ToolAccessInfo, GrantToolAccessRequest, CustomerPlatformConfig, UpdatePlatformConfigRequest, TextToSpeechProvider, CustomerTtsProvider, AssignTtsProviderRequest, SpeechToTextProvider, CustomerSttProvider, AssignSttProviderRequest, InitiateCustomerCreationRequest, RegistrationProfile, SubscriptionPlan, CustomerSubscription, CustomerToolFunctionAccessSummary } from '../../services/api';
import { toast } from 'sonner';
import { useTenantFilter } from '../../contexts/TenantFilterContext';
import { getCustomerPortalBaseUrl } from '../../utils/portalLink';
import { extractErrorMessage, createToastErrorHandler } from '../../utils/errorHandler';
import { formatSecondsAsMinutes, formatSecondsAsMmSs, secondsToBilledMinutes } from '../../utils/formatDuration';
import type { AdminFeature, CustomerFeatureListItem } from '../../services/api';

export function CustomersPage() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isImpersonating, setIsImpersonating] = useState<string | null>(null);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [page, setPage] = useState(1);
  const tenantFilter = useTenantFilter();
  const selectedTenant = tenantFilter.enabled ? tenantFilter.selectedTenant : null;
  const selectedTenantId = selectedTenant?.id;
  const [limit, setLimit] = useState(20);

  // Model access management state
  const [managingModelAccess, setManagingModelAccess] = useState<Customer | null>(null);
  const [customerModelAccess, setCustomerModelAccess] = useState<CustomerModelAccess[]>([]);
  const [availableModels, setAvailableModels] = useState<LLMModel[]>([]);
  const [selectedModels, setSelectedModels] = useState<Set<string>>(new Set());
  const [modelAccessLoading, setModelAccessLoading] = useState(false);

  // Tool access management state
  const [managingToolAccess, setManagingToolAccess] = useState<Customer | null>(null);
  const [customerToolAccess, setCustomerToolAccess] = useState<ToolAccessInfo[]>([]);
  const [availableTools, setAvailableTools] = useState<ToolDefinition[]>([]);
  const [selectedTools, setSelectedTools] = useState<Set<string>>(new Set());
  const [toolAccessLoading, setToolAccessLoading] = useState(false);

  // Feature-flag management state (CA-380) — embedded in the platform-config dialog
  const [availableFeatures, setAvailableFeatures] = useState<AdminFeature[]>([]);
  const [customerFeatureGrants, setCustomerFeatureGrants] = useState<
    Record<string, CustomerFeatureListItem>
  >({});
  const [initialFeatureSelection, setInitialFeatureSelection] = useState<Set<string>>(new Set());
  const [selectedFeatures, setSelectedFeatures] = useState<Set<string>>(new Set());
  const [featureExpiryDrafts, setFeatureExpiryDrafts] = useState<Record<string, string>>({});
  const [featureNotesDrafts, setFeatureNotesDrafts] = useState<Record<string, string>>({});

  // Tool function access management state
  const [expandedToolId, setExpandedToolId] = useState<string | null>(null);
  const [functionAccessSummaries, setFunctionAccessSummaries] = useState<Record<string, CustomerToolFunctionAccessSummary>>({});
  const [selectedFunctions, setSelectedFunctions] = useState<Record<string, Set<string>>>({});
  const [functionAccessLoading, setFunctionAccessLoading] = useState<string | null>(null);

  // Platform configuration management state
  const [managingPlatformConfig, setManagingPlatformConfig] = useState<Customer | null>(null);
  const [costOverviewCustomer, setCostOverviewCustomer] = useState<Customer | null>(null);
  const [platformConfig, setPlatformConfig] = useState<CustomerPlatformConfig | null>(null);
  const [platformConfigLoading, setPlatformConfigLoading] = useState(false);
  // Raw text of the retention-days field so the user can clear it while typing
  // (a number-only state would snap back and block re-entry). Validated on save.
  const [retentionDaysInput, setRetentionDaysInput] = useState<string>('');

  // TTS provider assignment management state
  const [managingTtsAccess, setManagingTtsAccess] = useState<Customer | null>(null);
  const [customerTtsProviders, setCustomerTtsProviders] = useState<CustomerTtsProvider[]>([]);
  const [availableTtsProviders, setAvailableTtsProviders] = useState<TextToSpeechProvider[]>([]);
  const [selectedTtsProviders, setSelectedTtsProviders] = useState<Set<string>>(new Set());
  const [ttsAccessLoading, setTtsAccessLoading] = useState(false);

  // STT provider assignment management state
  const [managingSttAccess, setManagingSttAccess] = useState<Customer | null>(null);
  const [customerSttProviders, setCustomerSttProviders] = useState<CustomerSttProvider[]>([]);
  const [availableSttProviders, setAvailableSttProviders] = useState<SpeechToTextProvider[]>([]);
  const [selectedSttProviders, setSelectedSttProviders] = useState<Set<string>>(new Set());
  const [sttAccessLoading, setSttAccessLoading] = useState(false);

  // Subscription plan assignment management state
  const [managingSubscription, setManagingSubscription] = useState<Customer | null>(null);
  const [availablePlans, setAvailablePlans] = useState<SubscriptionPlan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState<CustomerSubscription | null>(null);

  // Export state
  const [exporting, setExporting] = useState(false);

  // Suspend / Resume / Delete state
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [suspendLoading, setSuspendLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const navigate = useNavigate();

  // New customer creation dialog state
  const [showNewCustomerDialog, setShowNewCustomerDialog] = useState(false);
  const [newCustomerLoading, setNewCustomerLoading] = useState(false);
  const [registrationProfiles, setRegistrationProfiles] = useState<RegistrationProfile[]>([]);
  const [creationMethod, setCreationMethod] = useState<'direct' | 'request' | ''>('');
  const [newCustomerData, setNewCustomerData] = useState<InitiateCustomerCreationRequest>({
    companyName: '',
    description: '',
    ownerEmail: '',
    ownerName: '',
    ownerPhoneNumber: '',
    registrationProfileId: '',
  });

  // Sort configuration with localStorage persistence
  interface SortConfig {
    column: string;
    direction: 'asc' | 'desc';
  }

  const [sortConfig, setSortConfig] = useState<SortConfig>(() => {
    const saved = localStorage.getItem('admin-customers-sort');
    return saved ? JSON.parse(saved) : { column: 'name', direction: 'asc' };
  });

  // Save sort config to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('admin-customers-sort', JSON.stringify(sortConfig));
  }, [sortConfig]);

  // Globale Tenant-Auswahl gewechselt → zurück auf Seite 1
  useEffect(() => {
    setPage(1);
  }, [selectedTenantId]);

  // Reload customers when sort config, page, limit, searchTerm or tenant selection changes
  useEffect(() => {
    loadCustomers();
  }, [page, limit, searchTerm, sortConfig, selectedTenantId]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page,
        limit,
        sortBy: sortConfig.column,
        sortDirection: sortConfig.direction,
        ...(searchTerm && { search: searchTerm }),
        ...(selectedTenantId && { tenantId: selectedTenantId }),
      };

      const response = await apiService.getCustomers(params);
      setCustomers(response.customers);
      setTotalCustomers(response.total);
    } catch (err: any) {
      const errorMessage = extractErrorMessage(
        err,
        'Fehler beim Laden der Kundendaten.',
        'load-customers'
      );
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers;

  const getStatusBadge = (isActive: boolean) => {
    const variant = isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
    const label = isActive ? 'Aktiv' : 'Inaktiv';

    return (
      <Badge className={variant}>
        {label}
      </Badge>
    );
  };

  // Sortable table header component
  const SortableHeader = ({ column, label }: { column: string; label: string }) => (
    <th
      className="text-left py-2 px-4 font-medium text-gray-700 cursor-pointer hover:bg-gray-100 select-none"
      onClick={() => setSortConfig(prev => ({
        column,
        direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
      }))}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortConfig.column === column && (
          sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
        )}
      </div>
    </th>
  );

  const handleManageModelAccess = async (customer: Customer) => {
    setManagingModelAccess(customer);
    setModelAccessLoading(true);

    try {
      const [accessData, modelsData] = await Promise.all([
        apiService.getCustomerModelAccess(customer.id),
        apiService.getLlmModels({ includeInactive: false })
      ]);

      setCustomerModelAccess(accessData);
      setAvailableModels(modelsData);

      // The API returns model data with access info, not CustomerModelAccess entities
      // Each record represents an accessible model (API already filtered for enabled access), so all should be selected by default
      // Note: status field is the model status (active/beta/etc), not access status
      const accessibleModelIds = new Set(
        accessData.map(modelAccess => modelAccess.id)
      );
      setSelectedModels(accessibleModelIds);
    } catch (error: any) {
      console.error('Failed to load model access data:', error);
      const toastErrorHandler = createToastErrorHandler('load-model-access');
      toast.error('Fehler beim Laden der Modell-Zugriffsdaten', {
        description: toastErrorHandler(error, 'Unbekannter Fehler aufgetreten'),
        duration: 5000,
      });
    } finally {
      setModelAccessLoading(false);
    }
  };

  const handleSaveModelAccess = async () => {
    if (!managingModelAccess) return;

    setModelAccessLoading(true);

    try {
      const currentlyAccessible = new Set(
        customerModelAccess
          .filter(modelAccess => modelAccess.status === 'active')
          .map(modelAccess => modelAccess.id)
      );
      const toGrant = [...selectedModels].filter(modelId => !currentlyAccessible.has(modelId));
      const toRevoke = [...currentlyAccessible].filter(modelId => !selectedModels.has(modelId));

      const promises: Promise<any>[] = [];

      // Grant access to new models
      if (toGrant.length > 0) {
        const grantRequest: GrantModelAccessRequest = {
          modelIds: toGrant,
          options: {
            reason: 'Admin assignment via administration portal'
          }
        };
        promises.push(apiService.grantCustomerModelAccess(managingModelAccess.id, grantRequest));
      }

      // Revoke access from deselected models
      if (toRevoke.length > 0) {
        promises.push(apiService.revokeCustomerModelAccess(managingModelAccess.id, {
          modelIds: toRevoke,
          reason: 'Admin removal via administration portal'
        }));
      }

      if (promises.length > 0) {
        await Promise.all(promises);

        // Reload the access data
        const updatedAccessData = await apiService.getCustomerModelAccess(managingModelAccess.id);
        setCustomerModelAccess(updatedAccessData);

        toast.success(`Model access updated for ${managingModelAccess.name}`, {
          description: `${toGrant.length} models granted, ${toRevoke.length} models revoked`,
          duration: 4000,
        });
      } else {
        toast.info('No changes made to model access');
      }

    } catch (error: any) {
      console.error('Failed to update model access:', error);
      const toastErrorHandler = createToastErrorHandler('save');
      toast.error('Fehler beim Aktualisieren der Modell-Zugriffe', {
        description: toastErrorHandler(error, 'Unbekannter Fehler aufgetreten'),
        duration: 5000,
      });
    } finally {
      setModelAccessLoading(false);
    }
  };

  const handleGrantAllModels = async () => {
    if (!managingModelAccess) return;

    setModelAccessLoading(true);

    try {
      await apiService.grantAllModelsToCustomer(managingModelAccess.id);

      // Reload the access data
      const updatedAccessData = await apiService.getCustomerModelAccess(managingModelAccess.id);
      setCustomerModelAccess(updatedAccessData);

      // Update selected models
      const allModelIds = new Set(availableModels.map(model => model.id));
      setSelectedModels(allModelIds);

      toast.success(`All models granted to ${managingModelAccess.name}`, {
        duration: 4000,
      });

    } catch (error: any) {
      console.error('Failed to grant all models:', error);
      const toastErrorHandler = createToastErrorHandler('save');
      toast.error('Fehler beim Gewähren aller Modelle', {
        description: toastErrorHandler(error, 'Unbekannter Fehler aufgetreten'),
        duration: 5000,
      });
    } finally {
      setModelAccessLoading(false);
    }
  };

  // Tool Access Management Functions
  const handleManageToolAccess = async (customer: Customer) => {
    setManagingToolAccess(customer);
    setToolAccessLoading(true);

    try {
      const [accessData, toolsData] = await Promise.all([
        apiService.getCustomerToolAccess(customer.id),
        apiService.getToolDefinitions()
      ]);

      setCustomerToolAccess(accessData);
      setAvailableTools(toolsData);

      // Set selected tools based on current access
      const accessibleToolIds = new Set(
        accessData.filter(access => access.hasAccess).map(access => access.toolDefinitionId)
      );
      setSelectedTools(accessibleToolIds);
    } catch (error: any) {
      console.error('Failed to load tool access data:', error);
      const toastErrorHandler = createToastErrorHandler('load-tool-access');
      toast.error('Fehler beim Laden der Tool-Zugriffsdaten', {
        description: toastErrorHandler(error, 'Unbekannter Fehler aufgetreten'),
        duration: 5000,
      });
    } finally {
      setToolAccessLoading(false);
    }
  };

  const handleSaveToolAccess = async () => {
    if (!managingToolAccess) return;

    setToolAccessLoading(true);

    try {
      const currentlyAccessible = new Set(
        customerToolAccess
          .filter(access => access.hasAccess)
          .map(access => access.toolDefinitionId)
      );

      const toGrant = [...selectedTools].filter(toolId => !currentlyAccessible.has(toolId));
      const toRevoke = [...currentlyAccessible].filter(toolId => !selectedTools.has(toolId));

      const promises: Promise<any>[] = [];

      // Grant access to new tools
      for (const toolId of toGrant) {
        const grantRequest: GrantToolAccessRequest = {
          customerId: managingToolAccess.id
        };
        promises.push(apiService.grantToolAccess(toolId, grantRequest));
      }

      // Revoke access from deselected tools
      for (const toolId of toRevoke) {
        promises.push(apiService.revokeToolAccess(toolId, {
          customerId: managingToolAccess.id
        }));
      }

      if (promises.length > 0) {
        await Promise.all(promises);

        // Reload the access data
        const updatedAccessData = await apiService.getCustomerToolAccess(managingToolAccess.id);
        setCustomerToolAccess(updatedAccessData);

        toast.success(`Tool access updated for ${managingToolAccess.name}`, {
          description: `${toGrant.length} tools granted, ${toRevoke.length} tools revoked`,
          duration: 4000,
        });
      } else {
        toast.info('No changes made to tool access');
      }

    } catch (error: any) {
      console.error('Failed to update tool access:', error);
      const toastErrorHandler = createToastErrorHandler('save');
      toast.error('Fehler beim Aktualisieren der Tool-Zugriffe', {
        description: toastErrorHandler(error, 'Unbekannter Fehler aufgetreten'),
        duration: 5000,
      });
    } finally {
      setToolAccessLoading(false);
    }
  };

  const handleExpandToolFunctions = async (toolId: string) => {
    if (!managingToolAccess) return;

    if (expandedToolId === toolId) {
      setExpandedToolId(null);
      return;
    }

    setExpandedToolId(toolId);

    // Only load if not already cached
    if (!functionAccessSummaries[toolId]) {
      setFunctionAccessLoading(toolId);
      try {
        const summary = await apiService.getCustomerToolFunctionAccess(toolId, managingToolAccess.id);
        setFunctionAccessSummaries(prev => ({ ...prev, [toolId]: summary }));

        // Initialize selected functions from the summary
        const grantedIds = new Set(
          summary.functions.filter(f => f.hasAccess).map(f => f.toolFunctionId)
        );
        setSelectedFunctions(prev => ({ ...prev, [toolId]: grantedIds }));
      } catch (error: any) {
        console.error('Failed to load function access:', error);
        toast.error('Fehler beim Laden der Funktions-Zugriffe');
      } finally {
        setFunctionAccessLoading(null);
      }
    }
  };

  const handleSaveFunctionAccess = async (toolId: string) => {
    if (!managingToolAccess) return;

    const selected = selectedFunctions[toolId];
    if (!selected) return;

    setFunctionAccessLoading(toolId);
    try {
      const updatedSummary = await apiService.bulkUpdateFunctionAccess(
        toolId,
        managingToolAccess.id,
        { grantedFunctionIds: Array.from(selected) }
      );
      setFunctionAccessSummaries(prev => ({ ...prev, [toolId]: updatedSummary }));

      // Update selected functions from the response
      const grantedIds = new Set(
        updatedSummary.functions.filter(f => f.hasAccess).map(f => f.toolFunctionId)
      );
      setSelectedFunctions(prev => ({ ...prev, [toolId]: grantedIds }));

      toast.success('Funktions-Zugriffe aktualisiert', {
        description: `${grantedIds.size} von ${updatedSummary.totalFunctions} Funktionen aktiviert`,
        duration: 3000,
      });
    } catch (error: any) {
      console.error('Failed to update function access:', error);
      toast.error('Fehler beim Aktualisieren der Funktions-Zugriffe');
    } finally {
      setFunctionAccessLoading(null);
    }
  };

  const handleImpersonateCustomer = async (customer: Customer) => {
    if (isImpersonating) return; // Prevent multiple impersonations

    setIsImpersonating(customer.id);

    try {
      // Call impersonation API using the API service
      const impersonationData = await apiService.startImpersonation(
        customer.id,
        `Admin impersonation for customer support - ${customer.name}`
      );

      // Open customer portal in new tab with impersonation token
      const impersonationUrl = `${getCustomerPortalBaseUrl()}/?impersonation_token=${impersonationData.token}`;

      // Open in new tab
      window.open(impersonationUrl, '_blank', 'noopener,noreferrer');

      // Show success message
      toast.success(`Impersonation started for ${customer.name}`, {
        description: 'Customer portal opened in new tab',
        duration: 4000,
      });

    } catch (error: any) {
      console.error('Failed to start impersonation:', error);
      const errorMessage = extractErrorMessage(error, 'Fehler beim Starten der Identitätswechsel.');
      toast.error('Fehler beim Starten der Identitätswechsel', {
        description: errorMessage,
        duration: 5000,
      });
    } finally {
      setIsImpersonating(null);
    }
  };

  const handleManagePlatformConfig = async (customer: Customer) => {
    setManagingPlatformConfig(customer);
    setPlatformConfigLoading(true);

    try {
      const [config, allFeatures, customerGrants] = await Promise.all([
        apiService.getCustomerPlatformConfig(customer.id),
        apiService.listFeatures(),
        apiService.listCustomerFeatures(customer.id),
      ]);
      setPlatformConfig(config);
      setRetentionDaysInput(String(config.dataRetentionDays));
      setAvailableFeatures(allFeatures);

      const grantByFeature: Record<string, CustomerFeatureListItem> = {};
      const grantedIds = new Set<string>();
      const expiry: Record<string, string> = {};
      const notes: Record<string, string> = {};
      for (const g of customerGrants) {
        grantByFeature[g.featureId] = g;
        grantedIds.add(g.featureId);
        if (g.expiresAt) expiry[g.featureId] = g.expiresAt.slice(0, 10);
        if (g.notes) notes[g.featureId] = g.notes;
      }
      setCustomerFeatureGrants(grantByFeature);
      setInitialFeatureSelection(grantedIds);
      setSelectedFeatures(new Set(grantedIds));
      setFeatureExpiryDrafts(expiry);
      setFeatureNotesDrafts(notes);
    } catch (error: any) {
      console.error('Failed to load platform configuration:', error);
      const toastErrorHandler = createToastErrorHandler('load-platform-config');
      toast.error('Fehler beim Laden der Plattform-Konfiguration', {
        description: toastErrorHandler(error, 'Unbekannter Fehler aufgetreten'),
        duration: 5000,
      });
    } finally {
      setPlatformConfigLoading(false);
    }
  };

  const resetFeatureManagementState = () => {
    setAvailableFeatures([]);
    setCustomerFeatureGrants({});
    setInitialFeatureSelection(new Set());
    setSelectedFeatures(new Set());
    setFeatureExpiryDrafts({});
    setFeatureNotesDrafts({});
  };

  const toggleFeatureSelection = (featureId: string, checked: boolean) => {
    setSelectedFeatures(prev => {
      const next = new Set(prev);
      if (checked) next.add(featureId);
      else next.delete(featureId);
      return next;
    });
  };

  const handleSavePlatformConfig = async () => {
    if (!managingPlatformConfig || !platformConfig) return;

    // Validate retention days: must be an integer between 1 and 3650 (0 / empty not allowed)
    const retentionDays = parseInt(retentionDaysInput, 10);
    if (Number.isNaN(retentionDays) || retentionDays < 1 || retentionDays > 3650) {
      toast.error('Ungültige Aufbewahrungsfrist', {
        description: 'Die Aufbewahrungsfrist muss zwischen 1 und 3650 Tagen liegen.',
        duration: 5000,
      });
      return;
    }

    setPlatformConfigLoading(true);

    try {
      const updateData: UpdatePlatformConfigRequest = {
        showChatbots: platformConfig.showChatbots,
        showVoicebots: platformConfig.showVoicebots,
        showKnowledge: platformConfig.showKnowledge,
        showTemplates: platformConfig.showTemplates,
        showTools: platformConfig.showTools,
        showTestScenarios: platformConfig.showTestScenarios,
        showUserManagement: platformConfig.showUserManagement,
        showVoiceFunctionality: platformConfig.showVoiceFunctionality,
        showSpeechToText: platformConfig.showSpeechToText,
        showTimeoutsConfig: platformConfig.showTimeoutsConfig,
        showFeedbackFunctionality: platformConfig.showFeedbackFunctionality,
        showApiKeysManagement: platformConfig.showApiKeysManagement,
        dataRetentionDays: retentionDays,
        dataAnonymizationEnabled: platformConfig.dataAnonymizationEnabled,
      };

      // Diff feature grants → grant new / refresh changed / revoke removed
      const customerId = managingPlatformConfig.id;
      const toGrantOrUpdate: string[] = [];
      const toRevoke: string[] = [];

      for (const featureId of selectedFeatures) {
        if (!initialFeatureSelection.has(featureId)) {
          toGrantOrUpdate.push(featureId);
          continue;
        }
        const existingGrant = customerFeatureGrants[featureId];
        const draftExpiry = featureExpiryDrafts[featureId] ?? '';
        const existingExpiry = existingGrant?.expiresAt?.slice(0, 10) ?? '';
        const draftNotes = featureNotesDrafts[featureId] ?? '';
        const existingNotes = existingGrant?.notes ?? '';
        if (draftExpiry !== existingExpiry || draftNotes !== existingNotes) {
          toGrantOrUpdate.push(featureId);
        }
      }
      for (const featureId of initialFeatureSelection) {
        if (!selectedFeatures.has(featureId)) toRevoke.push(featureId);
      }

      const [updatedConfig] = await Promise.all([
        apiService.updateCustomerPlatformConfig(customerId, updateData),
        ...toGrantOrUpdate.map(featureId =>
          apiService.grantCustomerFeature(customerId, {
            featureId,
            expiresAt: featureExpiryDrafts[featureId]
              ? new Date(featureExpiryDrafts[featureId]).toISOString()
              : null,
            notes: featureNotesDrafts[featureId] || null,
          }),
        ),
        ...toRevoke.map(featureId =>
          apiService.revokeCustomerFeature(customerId, featureId),
        ),
      ]);

      setPlatformConfig(updatedConfig);
      setManagingPlatformConfig(null);
      resetFeatureManagementState();

      toast.success('Plattform-Konfiguration aktualisiert', {
        description: `Konfiguration für ${managingPlatformConfig.name} aktualisiert`,
        duration: 4000,
      });

    } catch (error: any) {
      console.error('Failed to update platform configuration:', error);
      const toastErrorHandler = createToastErrorHandler('save');
      toast.error('Fehler beim Aktualisieren der Plattform-Konfiguration', {
        description: toastErrorHandler(error, 'Unbekannter Fehler aufgetreten'),
        duration: 5000,
      });
    } finally {
      setPlatformConfigLoading(false);
    }
  };

  const handlePlatformConfigChange = (field: keyof UpdatePlatformConfigRequest, value: boolean) => {
    if (!platformConfig) return;

    setPlatformConfig({
      ...platformConfig,
      [field]: value
    });
  };

  // TTS Provider Access Management Functions
  const handleManageTtsAccess = async (customer: Customer) => {
    setManagingTtsAccess(customer);
    setTtsAccessLoading(true);

    try {
      const [assignedProviders, allProviders] = await Promise.all([
        apiService.getCustomerTtsProviders(customer.id),
        apiService.getTextToSpeechProviders()
      ]);

      setCustomerTtsProviders(assignedProviders);
      setAvailableTtsProviders(allProviders);

      // Set selected providers based on current assignments
      const assignedProviderIds = new Set(
        assignedProviders.filter(provider => provider.isActive).map(provider => provider.providerId)
      );
      setSelectedTtsProviders(assignedProviderIds);
    } catch (error: any) {
      console.error('Failed to load TTS provider access data:', error);
      const toastErrorHandler = createToastErrorHandler('load-tts-access');
      toast.error('Fehler beim Laden der TTS-Anbieter-Zugriffsdaten', {
        description: toastErrorHandler(error, 'Unbekannter Fehler aufgetreten'),
        duration: 5000,
      });
    } finally {
      setTtsAccessLoading(false);
    }
  };

  const handleSaveTtsAccess = async () => {
    if (!managingTtsAccess) return;

    setTtsAccessLoading(true);

    try {
      const currentlyAssigned = new Set(
        customerTtsProviders
          .filter(provider => provider.isActive)
          .map(provider => provider.providerId)
      );

      const toAssign = [...selectedTtsProviders].filter(providerId => !currentlyAssigned.has(providerId));
      const toRemove = [...currentlyAssigned].filter(providerId => !selectedTtsProviders.has(providerId));

      const promises: Promise<any>[] = [];

      // Assign new TTS providers
      for (const providerId of toAssign) {
        const assignRequest: AssignTtsProviderRequest = {
          providerId,
          isActive: true
        };
        promises.push(apiService.assignTtsProviderToCustomer(managingTtsAccess.id, assignRequest));
      }

      // Remove deselected TTS providers
      for (const providerId of toRemove) {
        promises.push(apiService.removeTtsProviderFromCustomer(managingTtsAccess.id, providerId));
      }

      if (promises.length > 0) {
        await Promise.all(promises);

        // Reload the assignment data
        const updatedProviders = await apiService.getCustomerTtsProviders(managingTtsAccess.id);
        setCustomerTtsProviders(updatedProviders);

        toast.success(`TTS provider access updated for ${managingTtsAccess.name}`, {
          description: `${toAssign.length} providers assigned, ${toRemove.length} providers removed`,
          duration: 4000,
        });
      } else {
        toast.info('No changes made to TTS provider access');
      }

    } catch (error: any) {
      console.error('Failed to update TTS provider access:', error);
      const toastErrorHandler = createToastErrorHandler('save');
      toast.error('Fehler beim Aktualisieren der TTS-Anbieter-Zugriffe', {
        description: toastErrorHandler(error, 'Unbekannter Fehler aufgetreten'),
        duration: 5000,
      });
    } finally {
      setTtsAccessLoading(false);
    }
  };

  // STT Provider Access Management Functions
  const handleManageSttAccess = async (customer: Customer) => {
    setManagingSttAccess(customer);
    setSttAccessLoading(true);

    try {
      const [assignedProviders, allProviders] = await Promise.all([
        apiService.getCustomerSttProviders(customer.id),
        apiService.getSpeechToTextProviders()
      ]);

      setCustomerSttProviders(assignedProviders);
      setAvailableSttProviders(allProviders);

      // Set selected providers based on current assignments
      const assignedProviderIds = new Set(
        assignedProviders.filter(provider => provider.isActive).map(provider => provider.providerId)
      );
      setSelectedSttProviders(assignedProviderIds);
    } catch (error: any) {
      console.error('Failed to load STT provider access data:', error);
      const toastErrorHandler = createToastErrorHandler('load-stt-access');
      toast.error('Fehler beim Laden der STT-Anbieter-Zugriffsdaten', {
        description: toastErrorHandler(error, 'Unbekannter Fehler aufgetreten'),
        duration: 5000,
      });
    } finally {
      setSttAccessLoading(false);
    }
  };

  const handleSaveSttAccess = async () => {
    if (!managingSttAccess) return;

    setSttAccessLoading(true);

    try {
      const currentlyAssigned = new Set(
        customerSttProviders
          .filter(provider => provider.isActive)
          .map(provider => provider.providerId)
      );

      const toAssign = [...selectedSttProviders].filter(providerId => !currentlyAssigned.has(providerId));
      const toRemove = [...currentlyAssigned].filter(providerId => !selectedSttProviders.has(providerId));

      const promises: Promise<any>[] = [];

      // Assign new STT providers
      for (const providerId of toAssign) {
        const assignRequest: AssignSttProviderRequest = {
          providerId,
          isActive: true
        };
        promises.push(apiService.assignSttProviderToCustomer(managingSttAccess.id, assignRequest));
      }

      // Remove deselected STT providers
      for (const providerId of toRemove) {
        promises.push(apiService.removeSttProviderFromCustomer(managingSttAccess.id, providerId));
      }

      if (promises.length > 0) {
        await Promise.all(promises);

        // Reload the assignment data
        const updatedProviders = await apiService.getCustomerSttProviders(managingSttAccess.id);
        setCustomerSttProviders(updatedProviders);

        toast.success(`STT provider access updated for ${managingSttAccess.name}`, {
          description: `${toAssign.length} providers assigned, ${toRemove.length} providers removed`,
          duration: 4000,
        });
      } else {
        toast.info('No changes made to STT provider access');
      }

    } catch (error: any) {
      console.error('Failed to update STT provider access:', error);
      const toastErrorHandler = createToastErrorHandler('save');
      toast.error('Fehler beim Aktualisieren der STT-Anbieter-Zugriffe', {
        description: toastErrorHandler(error, 'Unbekannter Fehler aufgetreten'),
        duration: 5000,
      });
    } finally {
      setSttAccessLoading(false);
    }
  };

  // Subscription Plan Assignment Functions
  const handleManageSubscription = async (customer: Customer) => {
    setManagingSubscription(customer);
    setSubscriptionLoading(true);
    setSelectedPlanId('');
    setCurrentSubscription(null);

    try {
      // Load available subscription plans and current subscription in parallel
      const [plans, subscription] = await Promise.all([
        apiService.getSubscriptionPlans(false),
        customer.subscription?.planName
          ? apiService.getCustomerSubscription(customer.id)
          : Promise.resolve(null),
      ]);
      setAvailablePlans(plans);
      setCurrentSubscription(subscription);
      setSelectedPlanId(subscription?.plan?.id ?? '');
    } catch (error: any) {
      console.error('Failed to load subscription plans:', error);
      const toastErrorHandler = createToastErrorHandler('load-subscription');
      toast.error('Fehler beim Laden der Abonnement-Pläne', {
        description: toastErrorHandler(error, 'Unbekannter Fehler aufgetreten'),
        duration: 5000,
      });
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleSaveSubscription = async () => {
    if (!managingSubscription || !selectedPlanId) return;

    setSubscriptionLoading(true);

    try {
      // Check if customer has existing subscription
      if (managingSubscription.subscription?.planName) {
        // Change existing subscription plan
        await apiService.changeCustomerPlan(managingSubscription.id, selectedPlanId);
        toast.success(`Abonnement für ${managingSubscription.name} geändert`, {
          description: 'Das Abonnement wurde erfolgreich aktualisiert',
          duration: 4000,
        });
      } else {
        // Create new subscription
        await apiService.createCustomerSubscription(managingSubscription.id, { planId: selectedPlanId });
        toast.success(`Abonnement für ${managingSubscription.name} erstellt`, {
          description: 'Das Abonnement wurde erfolgreich zugewiesen',
          duration: 4000,
        });
      }

      // Reload customers to reflect changes
      await loadCustomers();
      setManagingSubscription(null);

    } catch (error: any) {
      console.error('Failed to save subscription:', error);
      const toastErrorHandler = createToastErrorHandler('save-subscription');
      toast.error('Fehler beim Speichern des Abonnements', {
        description: toastErrorHandler(error, 'Unbekannter Fehler aufgetreten'),
        duration: 5000,
      });
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleSuspendCustomer = async () => {
    if (!managingSubscription) return;
    setSuspendLoading(true);
    try {
      const result = await apiService.suspendCustomer(managingSubscription.id, suspendReason || undefined);
      toast.success(`${managingSubscription.name} wurde pausiert`, {
        description: `${result.pausedAgents} Agent(s) wurden deaktiviert`,
        duration: 4000,
      });
      await loadCustomers();
      setSuspendDialogOpen(false);
      setSuspendReason('');
      setManagingSubscription(null);
    } catch (error: any) {
      const toastErrorHandler = createToastErrorHandler('suspend-customer');
      toast.error('Fehler beim Pausieren des Kontos', {
        description: toastErrorHandler(error, 'Unbekannter Fehler aufgetreten'),
        duration: 5000,
      });
    } finally {
      setSuspendLoading(false);
    }
  };

  const handleResumeCustomer = async () => {
    if (!managingSubscription) return;
    setSuspendLoading(true);
    try {
      const result = await apiService.resumeCustomer(managingSubscription.id);
      toast.success(`${managingSubscription.name} wurde reaktiviert`, {
        description: `${result.resumedAgents} Agent(s) wurden reaktiviert`,
        duration: 4000,
      });
      await loadCustomers();
      setResumeDialogOpen(false);
      setManagingSubscription(null);
    } catch (error: any) {
      const toastErrorHandler = createToastErrorHandler('resume-customer');
      toast.error('Fehler beim Reaktivieren des Kontos', {
        description: toastErrorHandler(error, 'Unbekannter Fehler aufgetreten'),
        duration: 5000,
      });
    } finally {
      setSuspendLoading(false);
    }
  };

  const handleDeleteCustomer = async () => {
    if (!managingSubscription) return;
    setDeleteLoading(true);
    try {
      await apiService.deleteCustomer(managingSubscription.id);
      toast.success(`${managingSubscription.name} wurde gelöscht`, {
        duration: 4000,
      });
      await loadCustomers();
      setDeleteDialogOpen(false);
      setManagingSubscription(null);
    } catch (error: any) {
      const toastErrorHandler = createToastErrorHandler('delete-customer');
      toast.error('Fehler beim Löschen des Kontos', {
        description: toastErrorHandler(error, 'Unbekannter Fehler aufgetreten'),
        duration: 5000,
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  // New customer creation handlers
  const handleOpenNewCustomerDialog = async () => {
    setShowNewCustomerDialog(true);
    setNewCustomerLoading(true);

    try {
      const profiles = await apiService.getActiveRegistrationProfiles();
      console.log('Loaded registration profiles:', profiles, 'Type:', typeof profiles, 'IsArray:', Array.isArray(profiles));
      setRegistrationProfiles(profiles);

      // Select default profile if available
      const defaultProfile = profiles.find(p => p.isDefault);
      if (defaultProfile) {
        setNewCustomerData(prev => ({ ...prev, registrationProfileId: defaultProfile.id }));
      }
    } catch (error: any) {
      console.error('Failed to load registration profiles:', error);
      const toastErrorHandler = createToastErrorHandler('load-profiles');
      toast.error('Fehler beim Laden der Registrierungsprofile', {
        description: toastErrorHandler(error, 'Unbekannter Fehler aufgetreten'),
        duration: 5000,
      });
    } finally {
      setNewCustomerLoading(false);
    }
  };

  const handleCreateCustomer = async () => {
    if (!newCustomerData.companyName || !newCustomerData.ownerEmail || !newCustomerData.ownerName || !newCustomerData.registrationProfileId || !creationMethod) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    setNewCustomerLoading(true);

    try {
      if (creationMethod === 'direct') {
        // Direct creation flow
        const response = await apiService.createCustomerDirectly(newCustomerData);

        toast.success('Customer erfolgreich erstellt!', {
          description: `Der Customer "${response.customerName}" wurde erstellt und eine Passwort-Email wurde an ${response.ownerEmail} gesendet.`,
          duration: 6000,
        });
      } else {
        // Registration request flow
        const response = await apiService.initiateCustomerCreation(newCustomerData);

        toast.success('Customer-Registrierung gestartet!', {
          description: `Eine Setup-Email wurde an ${response.ownerEmail} gesendet. Der Owner hat 24 Stunden Zeit, das Konto zu aktivieren.`,
          duration: 6000,
        });
      }

      // Reset form and close dialog
      setNewCustomerData({
        companyName: '',
        description: '',
        ownerEmail: '',
        ownerName: '',
        ownerPhoneNumber: '',
        registrationProfileId: '',
      });
      setCreationMethod('');
      setShowNewCustomerDialog(false);

      // Reload customers list
      await loadCustomers();

    } catch (error: any) {
      console.error('Failed to initiate customer creation:', error);
      const toastErrorHandler = createToastErrorHandler('create-customer');
      toast.error('Fehler beim Erstellen des Customers', {
        description: toastErrorHandler(error, 'Unbekannter Fehler aufgetreten'),
        duration: 5000,
      });
    } finally {
      setNewCustomerLoading(false);
    }
  };

  // Navigate to agent configuration page
  const handleManageChatbotRateLimits = (customer: Customer) => {
    navigate(`/customers/${customer.id}/agent-config`);
  };

  // Pagination handlers
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleLimitChange = (newLimit: string) => {
    setLimit(parseInt(newLimit));
    setPage(1); // Reset to first page when changing limit
  };

  const totalPages = Math.ceil(totalCustomers / limit);
  const startIndex = (page - 1) * limit + 1;
  const endIndex = Math.min(page * limit, totalCustomers);

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate start and end of visible range
      let start = Math.max(2, page - 1);
      let end = Math.min(totalPages - 1, page + 1);

      // Adjust range if at the beginning or end
      if (page <= 3) {
        end = 4;
      } else if (page >= totalPages - 2) {
        start = totalPages - 3;
      }

      // Add ellipsis before visible range if needed
      if (start > 2) {
        pages.push('...');
      }

      // Add visible page numbers
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      // Add ellipsis after visible range if needed
      if (end < totalPages - 1) {
        pages.push('...');
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  const handleExport = async (format: 'csv' | 'xlsx') => {
    try {
      setExporting(true);
      const blob = await apiService.exportCustomers({
        format,
        search: searchTerm || undefined,
        sortBy: sortConfig.column,
        sortDirection: sortConfig.direction,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kunden-export-${new Date().toISOString().slice(0, 10)}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Kundenliste als ${format.toUpperCase()} exportiert`);
    } catch (err: any) {
      toast.error(extractErrorMessage(err, 'Export fehlgeschlagen'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kundenverwaltung</h1>
          <p className="text-gray-600">
            {selectedTenant
              ? `Kunden von Tenant „${selectedTenant.name}" (Auswahl oben im Header)`
              : 'Übersicht und Verwaltung aller Platform-Kunden'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={exporting}>
                <Download className={`w-4 h-4 mr-2 ${exporting ? 'animate-pulse' : ''}`} />
                {exporting ? 'Exportieren...' : 'Export'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('csv')}>
                Als CSV exportieren
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('xlsx')}>
                Als Excel exportieren
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={handleOpenNewCustomerDialog}>
            <Plus className="w-4 h-4 mr-2" />
            Neuer Kunde
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Alle Kunden</CardTitle>
              <CardDescription>
                {loading ? 'Laden...' : `${totalCustomers} Kunden insgesamt`}
              </CardDescription>
            </div>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Kunden suchen (Name, E-Mail)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="flex items-center justify-center p-8 text-red-600">
              <AlertCircle className="w-5 h-5 mr-2" />
              <span>{error}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={loadCustomers}
                className="ml-4"
              >
                Erneut versuchen
              </Button>
            </div>
          )}

          {loading && !error && (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span>Kunden werden geladen...</span>
            </div>
          )}

          {!loading && !error && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <SortableHeader column="name" label="Kunde" />
                    <SortableHeader column="plan" label="Plan" />
                    <th className="text-left py-2 px-4 font-medium text-gray-700">Status</th>
                    <SortableHeader column="activeChatbots" label="Chatbots" />
                    <SortableHeader column="activeVoicebots" label="Voicebots" />
                    <SortableHeader column="phoneNumbers" label="Rufnummern" />
                    <SortableHeader column="callMinutes" label="Telefonminuten" />
                    <SortableHeader column="createdAt" label="Erstellt" />
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                {filteredCustomers.map((customer) => (
                  <tr key={customer.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-1.5 px-4">
                      <div>
                        <div className="font-medium text-gray-900">{customer.name}</div>
                        <div className="text-sm text-gray-600">{customer.ownerEmail || 'Keine E-Mail'}</div>
                      </div>
                    </td>
                    <td className="py-1.5 px-4">
                      <span className="font-medium text-gray-900">
                        {customer.subscription?.planName || 'Kein Plan'}
                      </span>
                    </td>
                    <td className="py-1.5 px-4">
                      {customer.subscription?.status === 'suspended' ? (
                        <Badge variant="outline" className="border-red-300 bg-red-50 text-red-700">Pausiert</Badge>
                      ) : customer.subscription?.status === 'active' ? (
                        <Badge variant="outline" className="border-green-300 bg-green-50 text-green-700">Aktiv</Badge>
                      ) : customer.subscription?.status === 'cancelled' ? (
                        <Badge variant="outline" className="border-gray-300 bg-gray-50 text-gray-700">Gekündigt</Badge>
                      ) : customer.subscription?.status === 'expired' ? (
                        <Badge variant="outline" className="border-yellow-300 bg-yellow-50 text-yellow-700">Abgelaufen</Badge>
                      ) : (
                        <Badge variant="outline" className="border-gray-300 bg-gray-50 text-gray-500">—</Badge>
                      )}
                    </td>
                    <td className="py-1.5 px-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Bot className="w-4 h-4 text-purple-600" />
                        <span className="font-medium text-gray-900">{customer.stats.activeChatbots}</span>
                      </div>
                    </td>
                    <td className="py-1.5 px-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Bot className="w-4 h-4 text-orange-600" />
                        <span className="font-medium text-gray-900">{customer.stats.activeVoicebots}</span>
                      </div>
                    </td>
                    <td className="py-1.5 px-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Phone className="w-4 h-4 text-blue-600" />
                        <span className="font-medium text-gray-900">{customer.stats.phoneNumbers}</span>
                      </div>
                    </td>
                    <td className="py-1.5 px-4">
                      {customer.subscription ? (
                        <div className="text-sm space-y-1">
                          <div className="font-medium text-gray-900">
                            {formatSecondsAsMinutes(customer.subscription.usedCallSeconds)}
                            {customer.subscription.maxCallMinutes !== null && (
                              <span className="text-gray-500 font-normal"> / {customer.subscription.maxCallMinutes.toLocaleString('de-DE')}</span>
                            )}
                          </div>
                          {customer.subscription.maxCallMinutes !== null && (() => {
                            const usedMinutes = secondsToBilledMinutes(customer.subscription.usedCallSeconds);
                            const ratio = usedMinutes / customer.subscription.maxCallMinutes!;
                            return (
                              <Progress
                                value={Math.min(ratio * 100, 100)}
                                className={`h-1.5 ${
                                  ratio > 0.9
                                    ? '[&>[data-slot=progress-indicator]]:bg-red-500'
                                    : ratio > 0.7
                                      ? '[&>[data-slot=progress-indicator]]:bg-yellow-500'
                                      : '[&>[data-slot=progress-indicator]]:bg-green-500'
                                }`}
                              />
                            );
                          })()}
                        </div>
                      ) : (
                        <span className="text-gray-400">&ndash;</span>
                      )}
                    </td>
                    <td className="py-1.5 px-4 text-gray-600">
                      {new Date(customer.createdAt).toLocaleDateString('de-DE')}
                    </td>
                    <td className="py-1.5 px-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <Dialog>
                            <DialogTrigger asChild>
                              <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setSelectedCustomer(customer); }}>
                                <Eye className="w-4 h-4 mr-2" />
                                Details anzeigen
                              </DropdownMenuItem>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl">
                              <DialogHeader>
                                <DialogTitle>{selectedCustomer?.name}</DialogTitle>
                                <DialogDescription>
                                  Detaillierte Kundenstatistiken und Informationen
                                </DialogDescription>
                              </DialogHeader>
                              {selectedCustomer && (
                                <div className="space-y-6">
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <h4 className="text-sm font-medium text-gray-700 mb-2">Grundinformationen</h4>
                                      <div className="space-y-2 text-sm">
                                        <div><strong>Status:</strong> {getStatusBadge(selectedCustomer.isActive)}</div>
                                        <div><strong>Plan:</strong> {selectedCustomer.subscription?.planName || 'Kein Plan'}</div>
                                        <div><strong>Beschreibung:</strong> {selectedCustomer.description || 'Keine Beschreibung'}</div>
                                        <div><strong>Erstellt:</strong> {new Date(selectedCustomer.createdAt).toLocaleDateString('de-DE')}</div>
                                        {selectedCustomer.subscription?.billingAddress && (
                                          <div className="mt-3 pt-3 border-t">
                                            <strong>Rechnungsanschrift:</strong>
                                            <div className="text-gray-600 mt-1">
                                              <div>{selectedCustomer.subscription.billingAddress.company}</div>
                                              <div>{selectedCustomer.subscription.billingAddress.name}</div>
                                              <div>{selectedCustomer.subscription.billingAddress.street}</div>
                                              <div>{selectedCustomer.subscription.billingAddress.zip} {selectedCustomer.subscription.billingAddress.city}</div>
                                              <div>{selectedCustomer.subscription.billingAddress.country}</div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div>
                                      <h4 className="text-sm font-medium text-gray-700 mb-2">Nutzungsstatistiken</h4>
                                      <div className="grid grid-cols-2 gap-4">
                                        <div className="flex items-center space-x-2">
                                          <Bot className="w-4 h-4 text-purple-600" />
                                          <div>
                                            <div className="text-lg font-bold">{selectedCustomer.stats.activeChatbots}</div>
                                            <div className="text-xs text-gray-600">Aktive Chatbots</div>
                                          </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                          <Bot className="w-4 h-4 text-orange-600" />
                                          <div>
                                            <div className="text-lg font-bold">{selectedCustomer.stats.activeVoicebots}</div>
                                            <div className="text-xs text-gray-600">Aktive Voicebots</div>
                                          </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                          <Phone className="w-4 h-4 text-blue-600" />
                                          <div>
                                            <div className="text-lg font-bold">{selectedCustomer.stats.phoneNumbers}</div>
                                            <div className="text-xs text-gray-600">Rufnummern</div>
                                          </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                          <BarChart3 className="w-4 h-4 text-indigo-600" />
                                          <div>
                                            <div className="text-lg font-bold">
                                              {selectedCustomer.subscription
                                                ? formatSecondsAsMmSs(selectedCustomer.subscription.usedCallSeconds)
                                                : selectedCustomer.stats.callMinutesTotal.toLocaleString('de-DE')}
                                            </div>
                                            <div className="text-xs text-gray-600">
                                              {selectedCustomer.subscription
                                                ? `Telefonzeit${selectedCustomer.subscription.maxCallMinutes !== null ? ` / ${selectedCustomer.subscription.maxCallMinutes.toLocaleString('de-DE')} min` : ''}`
                                                : 'Telefonminuten gesamt'}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </DialogContent>
                          </Dialog>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleManageModelAccess(customer)}>
                            <Settings className="w-4 h-4 mr-2" />
                            LLM-Modelle
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleManageToolAccess(customer)}>
                            <Wrench className="w-4 h-4 mr-2" />
                            Tools
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleManagePlatformConfig(customer)}>
                            <Monitor className="w-4 h-4 mr-2" />
                            Plattform-Konfig
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleManageTtsAccess(customer)}>
                            <Volume2 className="w-4 h-4 mr-2" />
                            TTS-Provider
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleManageSttAccess(customer)}>
                            <Mic className="w-4 h-4 mr-2" />
                            STT-Provider
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleManageSubscription(customer)}>
                            <CreditCard className="w-4 h-4 mr-2" />
                            Abonnement
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setCostOverviewCustomer(customer)}>
                            <DollarSign className="w-4 h-4 mr-2" />
                            Kostenübersicht
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleManageChatbotRateLimits(customer)}>
                            <Shield className="w-4 h-4 mr-2" />
                            Agent Konfiguration
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleImpersonateCustomer(customer)}
                            disabled={isImpersonating === customer.id}
                          >
                            {isImpersonating === customer.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                            ) : (
                              <ExternalLink className="w-4 h-4 mr-2" />
                            )}
                            Impersonieren
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
                </tbody>
              </table>

              {/* Pagination Controls */}
              {!loading && !error && totalCustomers > 0 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                  {/* Left side - Page size selector and info */}
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">Einträge pro Seite:</span>
                      <Select value={limit.toString()} onValueChange={handleLimitChange}>
                        <SelectTrigger className="w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="20">20</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="text-sm text-gray-600">
                      {startIndex}-{endIndex} von {totalCustomers} Kunden
                    </div>
                  </div>

                  {/* Right side - Page navigation */}
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>

                    {getPageNumbers().map((pageNum, index) => {
                      if (pageNum === '...') {
                        return (
                          <span key={`ellipsis-${index}`} className="px-2 text-gray-400">
                            ...
                          </span>
                        );
                      }

                      return (
                        <Button
                          key={pageNum}
                          variant={page === pageNum ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handlePageChange(pageNum as number)}
                          className="min-w-[2.5rem]"
                        >
                          {pageNum}
                        </Button>
                      );
                    })}

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* LLM Model Access Management Dialog */}
      <Dialog
        open={!!managingModelAccess}
        onOpenChange={() => {
          setManagingModelAccess(null);
          setCustomerModelAccess([]);
          setAvailableModels([]);
          setSelectedModels(new Set());
        }}
      >
        <DialogContent className="max-w-2xl sm:max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center space-x-2">
              <Settings className="w-5 h-5 text-purple-600" />
              <span>LLM Modelle verwalten - {managingModelAccess?.name}</span>
            </DialogTitle>
            <DialogDescription>
              Verwalten Sie die Zugriffe auf LLM Modelle für diesen Kunden.
              Ausgewählte Modelle sind für die Agents des Kunden verfügbar.
            </DialogDescription>
          </DialogHeader>

          {modelAccessLoading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span>Lade Modell-Zugriffe...</span>
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col">
              {/* Scrollable body — only this area scrolls, the footer stays pinned */}
              <div className="min-h-0 flex-1 space-y-6 overflow-y-auto pr-1">
              {/* Action Buttons */}
              <div className="flex items-center justify-between border-b pb-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700">
                    {selectedModels.size} von {availableModels.length} Modellen ausgewählt
                  </h4>
                  <p className="text-xs text-gray-500 mt-1">
                    Wählen Sie die Modelle aus, die dieser Kunde verwenden darf
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedModels(new Set())}
                    disabled={selectedModels.size === 0}
                  >
                    Alle abwählen
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedModels(new Set(availableModels.map(m => m.id)))}
                    disabled={selectedModels.size === availableModels.length}
                  >
                    Alle auswählen
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleGrantAllModels}
                    disabled={modelAccessLoading}
                  >
                    {modelAccessLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Alle gewähren
                  </Button>
                </div>
              </div>

              {/* Models grouped by provider — compact, scannable table per provider */}
              {(() => {
                // Group the flat model list by provider so each provider gets its own table block
                const groups = new Map<string, { providerName: string; models: LLMModel[] }>();
                for (const model of availableModels) {
                  const key = model.provider?.id ?? model.providerId;
                  const providerName = model.provider?.displayName ?? model.provider?.name ?? 'Unbekannter Provider';
                  const existing = groups.get(key);
                  if (existing) {
                    existing.models.push(model);
                  } else {
                    groups.set(key, { providerName, models: [model] });
                  }
                }
                const sortedGroups = [...groups.values()].sort((a, b) =>
                  a.providerName.localeCompare(b.providerName, 'de')
                );

                if (sortedGroups.length === 0) {
                  return (
                    <div className="py-8 text-center text-sm text-gray-500">
                      Keine Modelle verfügbar
                    </div>
                  );
                }

                return (
                  <div className="space-y-6">
                    {sortedGroups.map((group) => {
                      const groupModelIds = group.models.map(m => m.id);
                      const selectedInGroup = groupModelIds.filter(id => selectedModels.has(id)).length;
                      const allSelected = selectedInGroup === groupModelIds.length;

                      return (
                        <div key={group.providerName} className="overflow-hidden rounded-lg border border-gray-200">
                          {/* Provider group header with select-all for the whole provider */}
                          <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-2">
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                checked={allSelected}
                                onCheckedChange={(checked) => {
                                  const newSelected = new Set(selectedModels);
                                  if (checked) {
                                    groupModelIds.forEach(id => newSelected.add(id));
                                  } else {
                                    groupModelIds.forEach(id => newSelected.delete(id));
                                  }
                                  setSelectedModels(newSelected);
                                }}
                              />
                              <span className="text-sm font-semibold text-gray-800">{group.providerName}</span>
                              <Badge variant="outline" className="text-xs">{group.models.length}</Badge>
                            </div>
                            <span className="text-xs text-gray-500">{selectedInGroup} ausgewählt</span>
                          </div>

                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-10"></TableHead>
                                <TableHead>Modell</TableHead>
                                <TableHead className="w-28">Zugriff</TableHead>
                                <TableHead className="w-16 text-center">Details</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {group.models.map((model) => {
                                const isSelected = selectedModels.has(model.id);
                                const hasAccess = customerModelAccess.find(access => access.id === model.id);
                                const toggle = () => {
                                  const newSelected = new Set(selectedModels);
                                  if (isSelected) {
                                    newSelected.delete(model.id);
                                  } else {
                                    newSelected.add(model.id);
                                  }
                                  setSelectedModels(newSelected);
                                };

                                // Capabilities/cost: prefer the customer-specific access record, fall back to model defaults
                                const supportsTools = hasAccess ? hasAccess.capabilities.supportsTools : model.supportsTools;
                                const supportsVision = hasAccess ? hasAccess.capabilities.supportsVision : model.supportsVision;
                                const supportsStreaming = hasAccess ? hasAccess.capabilities.supportsStreaming : model.supportsStreaming;
                                const hasAnyFeature = supportsTools || supportsVision || supportsStreaming;
                                const inputCost = hasAccess?.cost ? hasAccess.cost.inputCostPerMillionTokensEur : model.inputCostPerMillionTokensEur;
                                const outputCost = hasAccess?.cost ? hasAccess.cost.outputCostPerMillionTokensEur : model.outputCostPerMillionTokensEur;

                                return (
                                  <TableRow
                                    key={model.id}
                                    className={`cursor-pointer ${isSelected ? 'bg-purple-50' : ''}`}
                                    onClick={toggle}
                                  >
                                    <TableCell onClick={(e) => { e.stopPropagation(); }}>
                                      <Checkbox checked={isSelected} onCheckedChange={toggle} />
                                    </TableCell>
                                    <TableCell>
                                      <div className="font-medium text-gray-900">{model.displayName}</div>
                                      <div className="text-xs text-gray-500">{model.modelKey}</div>
                                    </TableCell>
                                    <TableCell>
                                      {hasAccess && hasAccess.status === 'active' ? (
                                        <Badge variant="outline" className="border-green-200 text-green-700">
                                          <CheckCircle className="mr-1 h-3 w-3" /> Aktiv
                                        </Badge>
                                      ) : hasAccess && hasAccess.status !== 'active' ? (
                                        <Badge variant="outline" className="border-red-200 text-red-700">
                                          <XCircle className="mr-1 h-3 w-3" /> Inaktiv
                                        </Badge>
                                      ) : (
                                        <span className="text-xs text-gray-400">—</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-center" onClick={(e) => { e.stopPropagation(); }}>
                                      <Popover>
                                        <PopoverTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 w-7 p-0 text-gray-400 hover:text-gray-700"
                                            title="Details anzeigen"
                                          >
                                            <HelpCircle className="h-4 w-4" />
                                          </Button>
                                        </PopoverTrigger>
                                        <PopoverContent align="end" className="w-72 text-xs">
                                          <div className="space-y-3">
                                            <div>
                                              <div className="mb-1 font-medium text-gray-700">Features</div>
                                              <div className="flex flex-wrap gap-1">
                                                {supportsTools && <Badge variant="outline" className="px-1 py-0 text-xs">Tools</Badge>}
                                                {supportsVision && <Badge variant="outline" className="px-1 py-0 text-xs">Vision</Badge>}
                                                {supportsStreaming && <Badge variant="outline" className="px-1 py-0 text-xs">Streaming</Badge>}
                                                {!hasAnyFeature && <span className="text-gray-400">Keine</span>}
                                              </div>
                                            </div>
                                            <div>
                                              <div className="mb-1 font-medium text-gray-700">Kosten (pro 1 Mio. Tokens)</div>
                                              <div className="space-y-0.5 text-gray-600">
                                                <div className="flex justify-between"><span>Input</span><span>€{inputCost}</span></div>
                                                <div className="flex justify-between"><span>Output</span><span>€{outputCost}</span></div>
                                              </div>
                                            </div>
                                            {hasAccess && (
                                              <div>
                                                <div className="mb-1 font-medium text-gray-700">Nutzung (Monat)</div>
                                                <div className="space-y-0.5 text-gray-600">
                                                  <div className="flex justify-between">
                                                    <span>Tokens</span>
                                                    <span>
                                                      {hasAccess.limits.currentMonthTokens.toLocaleString('de-DE')}
                                                      {hasAccess.limits.monthlyTokenLimit && ` / ${hasAccess.limits.monthlyTokenLimit.toLocaleString('de-DE')}`}
                                                    </span>
                                                  </div>
                                                  <div className="flex justify-between">
                                                    <span>Anfragen</span>
                                                    <span>
                                                      {hasAccess.limits.currentMonthRequests.toLocaleString('de-DE')}
                                                      {hasAccess.limits.monthlyRequestLimit && ` / ${hasAccess.limits.monthlyRequestLimit.toLocaleString('de-DE')}`}
                                                    </span>
                                                  </div>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </PopoverContent>
                                      </Popover>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              </div>
              {/* Footer Actions — pinned to the bottom, outside the scroll area */}
              <div className="flex shrink-0 justify-end space-x-2 border-t pt-4 mt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setManagingModelAccess(null);
                    setCustomerModelAccess([]);
                    setAvailableModels([]);
                    setSelectedModels(new Set());
                  }}
                  disabled={modelAccessLoading}
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={handleSaveModelAccess}
                  disabled={modelAccessLoading}
                >
                  {modelAccessLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Änderungen speichern
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Tool Access Management Dialog */}
      <Dialog
        open={!!managingToolAccess}
        onOpenChange={() => {
          setManagingToolAccess(null);
          setCustomerToolAccess([]);
          setAvailableTools([]);
          setSelectedTools(new Set());
          setExpandedToolId(null);
          setFunctionAccessSummaries({});
          setSelectedFunctions({});
          setFunctionAccessLoading(null);
        }}
      >
        <DialogContent className="max-w-2xl sm:max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center space-x-2">
              <Wrench className="w-5 h-5 text-green-600" />
              <span>Tool-Zugriff verwalten - {managingToolAccess?.name}</span>
            </DialogTitle>
            <DialogDescription>
              Verwalten Sie die Zugriffe auf Tools für diesen Kunden.
              Ausgewählte Tools sind für die Agents des Kunden verfügbar.
            </DialogDescription>
          </DialogHeader>

          {toolAccessLoading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span>Lade Tool-Zugriffe...</span>
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col">
              {/* Scrollable body — only this area scrolls, the footer stays pinned */}
              <div className="min-h-0 flex-1 space-y-6 overflow-y-auto pr-1">
              {/* Action Buttons */}
              <div className="flex items-center justify-between border-b pb-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700">
                    {selectedTools.size} von {availableTools.length} Tools ausgewählt
                  </h4>
                  <p className="text-xs text-gray-500 mt-1">
                    Wählen Sie die Tools aus, die dieser Kunde verwenden darf
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedTools(new Set())}
                    disabled={selectedTools.size === 0}
                  >
                    Alle abwählen
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedTools(new Set(availableTools.map(t => t.id)))}
                    disabled={selectedTools.size === availableTools.length}
                  >
                    Alle auswählen
                  </Button>
                </div>
              </div>

              {/* Tools grouped by category — compact, scannable table per category */}
              {(() => {
                // Group the flat tool list by category so each category gets its own table block
                const groups = new Map<string, ToolDefinition[]>();
                for (const tool of availableTools) {
                  const key = tool.category;
                  const existing = groups.get(key);
                  if (existing) {
                    existing.push(tool);
                  } else {
                    groups.set(key, [tool]);
                  }
                }
                const sortedGroups = [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0], 'de'));

                if (sortedGroups.length === 0) {
                  return (
                    <div className="py-8 text-center text-sm text-gray-500">
                      Keine Tools verfügbar
                    </div>
                  );
                }

                return (
                  <div className="space-y-6">
                    {sortedGroups.map(([category, groupTools]) => {
                      const groupIds = groupTools.map(t => t.id);
                      const selectedInGroup = groupIds.filter(id => selectedTools.has(id)).length;
                      const allSelected = selectedInGroup === groupIds.length;

                      return (
                        <div key={category} className="overflow-hidden rounded-lg border border-gray-200">
                          {/* Category group header with select-all for the whole category */}
                          <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-2">
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                checked={allSelected}
                                onCheckedChange={(checked) => {
                                  const newSelected = new Set(selectedTools);
                                  if (checked) {
                                    groupIds.forEach(id => newSelected.add(id));
                                  } else {
                                    groupIds.forEach(id => newSelected.delete(id));
                                  }
                                  setSelectedTools(newSelected);
                                }}
                              />
                              <span className="text-sm font-semibold capitalize text-gray-800">{category}</span>
                              <Badge variant="outline" className="text-xs">{groupTools.length}</Badge>
                            </div>
                            <span className="text-xs text-gray-500">{selectedInGroup} ausgewählt</span>
                          </div>

                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-10"></TableHead>
                                <TableHead>Tool</TableHead>
                                <TableHead className="w-28">Zugriff</TableHead>
                                <TableHead className="w-16 text-center">Details</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {groupTools.map((tool) => {
                                const isSelected = selectedTools.has(tool.id);
                                const hasAccess = customerToolAccess.find(access => access.toolDefinitionId === tool.id && access.hasAccess);
                                const summary = functionAccessSummaries[tool.id];
                                const isExpanded = expandedToolId === tool.id;
                                const toggle = () => {
                                  const newSelected = new Set(selectedTools);
                                  if (isSelected) {
                                    newSelected.delete(tool.id);
                                  } else {
                                    newSelected.add(tool.id);
                                  }
                                  setSelectedTools(newSelected);
                                };

                                return (
                                  <Fragment key={tool.id}>
                                    <TableRow
                                      className={`cursor-pointer ${isSelected ? 'bg-green-50' : ''}`}
                                      onClick={toggle}
                                    >
                                      <TableCell onClick={(e) => { e.stopPropagation(); }}>
                                        <Checkbox checked={isSelected} onCheckedChange={toggle} />
                                      </TableCell>
                                      <TableCell>
                                        <div className="font-medium text-gray-900">{tool.displayName}</div>
                                        <div className="text-xs text-gray-500">{tool.category} • {tool.version}</div>
                                        {/* Function-level access toggle for tools with functions (only when selected) */}
                                        {isSelected && tool.usesFunctions && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="mt-1 h-6 px-1 text-xs"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              void handleExpandToolFunctions(tool.id);
                                            }}
                                          >
                                            <Settings className="mr-1 h-3 w-3" />
                                            Funktionen
                                            {summary && (
                                              <Badge variant={summary.hasFunctionLevelRestrictions ? 'secondary' : 'outline'} className="ml-1 px-1 py-0 text-xs">
                                                {summary.grantedFunctions}/{summary.totalFunctions}
                                              </Badge>
                                            )}
                                            {isExpanded ? <ChevronUp className="ml-1 h-3 w-3" /> : <ChevronDown className="ml-1 h-3 w-3" />}
                                          </Button>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        {hasAccess ? (
                                          <Badge variant="outline" className="border-green-200 text-green-700">
                                            <CheckCircle className="mr-1 h-3 w-3" /> Aktiv
                                          </Badge>
                                        ) : (
                                          <span className="text-xs text-gray-400">—</span>
                                        )}
                                      </TableCell>
                                      <TableCell className="text-center" onClick={(e) => { e.stopPropagation(); }}>
                                        <Popover>
                                          <PopoverTrigger asChild>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-7 w-7 p-0 text-gray-400 hover:text-gray-700"
                                              title="Details anzeigen"
                                            >
                                              <HelpCircle className="h-4 w-4" />
                                            </Button>
                                          </PopoverTrigger>
                                          <PopoverContent align="end" className="w-72 text-xs">
                                            <div className="space-y-3">
                                              {tool.description && (
                                                <div className="text-gray-600">{tool.description}</div>
                                              )}
                                              <div className="space-y-0.5 text-gray-600">
                                                <div className="flex justify-between">
                                                  <span>Sichtbarkeit</span>
                                                  <Badge variant={tool.isPublic ? 'default' : 'secondary'} className="text-xs">
                                                    {tool.isPublic ? 'Öffentlich' : 'Privat'}
                                                  </Badge>
                                                </div>
                                                <div className="flex justify-between"><span>Nutzung</span><span>{tool.usageCount ?? 0}</span></div>
                                                <div className="flex justify-between"><span>Agents</span><span>{tool.agentCount}</span></div>
                                              </div>
                                              {tool.tags && tool.tags.length > 0 && (
                                                <div>
                                                  <div className="mb-1 font-medium text-gray-700">Tags</div>
                                                  <div className="flex flex-wrap gap-1">
                                                    {tool.tags.slice(0, 6).map((tag, index) => (
                                                      <Badge key={index} variant="outline" className="px-1 py-0 text-xs">{tag}</Badge>
                                                    ))}
                                                    {tool.tags.length > 6 && (
                                                      <Badge variant="outline" className="px-1 py-0 text-xs">+{tool.tags.length - 6}</Badge>
                                                    )}
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          </PopoverContent>
                                        </Popover>
                                      </TableCell>
                                    </TableRow>

                                    {/* Expandable function-level access sub-row */}
                                    {isSelected && tool.usesFunctions && isExpanded && (
                                      <TableRow className="bg-gray-50/50 hover:bg-gray-50/50">
                                        <TableCell colSpan={4} className="py-3">
                                          {functionAccessLoading === tool.id ? (
                                            <div className="flex items-center justify-center py-3">
                                              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                                              <span className="text-xs text-gray-500">Laden...</span>
                                            </div>
                                          ) : summary ? (
                                            <div className="space-y-2">
                                              <div className="flex items-center justify-between">
                                                <span className="text-xs text-gray-500">
                                                  {selectedFunctions[tool.id]?.size ?? 0} von {summary.totalFunctions} aktiv
                                                </span>
                                                <div className="flex items-center space-x-1">
                                                  <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-5 px-1 text-xs"
                                                    onClick={() => {
                                                      const allIds = new Set(summary.functions.map(f => f.toolFunctionId));
                                                      setSelectedFunctions(prev => ({ ...prev, [tool.id]: allIds }));
                                                    }}
                                                  >
                                                    Alle
                                                  </Button>
                                                  <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-5 px-1 text-xs"
                                                    onClick={() => {
                                                      setSelectedFunctions(prev => ({ ...prev, [tool.id]: new Set() }));
                                                    }}
                                                  >
                                                    Keine
                                                  </Button>
                                                </div>
                                              </div>
                                              <div className="max-h-40 space-y-1 overflow-y-auto">
                                                {summary.functions.map((fn) => {
                                                  const fnSelected = selectedFunctions[tool.id]?.has(fn.toolFunctionId) ?? false;
                                                  return (
                                                    <label
                                                      key={fn.toolFunctionId}
                                                      className="flex cursor-pointer items-center space-x-2 rounded px-1 py-1 hover:bg-gray-100"
                                                    >
                                                      <Checkbox
                                                        checked={fnSelected}
                                                        onCheckedChange={(checked) => {
                                                          const current = new Set(selectedFunctions[tool.id] ?? []);
                                                          if (checked) {
                                                            current.add(fn.toolFunctionId);
                                                          } else {
                                                            current.delete(fn.toolFunctionId);
                                                          }
                                                          setSelectedFunctions(prev => ({ ...prev, [tool.id]: current }));
                                                        }}
                                                      />
                                                      <span className="truncate text-xs" title={fn.functionName}>
                                                        {fn.functionDisplayName}
                                                      </span>
                                                    </label>
                                                  );
                                                })}
                                              </div>
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                className="mt-1 h-7 w-full text-xs"
                                                onClick={() => handleSaveFunctionAccess(tool.id)}
                                                disabled={functionAccessLoading === tool.id}
                                              >
                                                {functionAccessLoading === tool.id && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                                                Funktionen speichern
                                              </Button>
                                            </div>
                                          ) : null}
                                        </TableCell>
                                      </TableRow>
                                    )}
                                  </Fragment>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              </div>
              {/* Footer Actions — pinned to the bottom, outside the scroll area */}
              <div className="flex shrink-0 justify-end space-x-2 border-t pt-4 mt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setManagingToolAccess(null);
                    setCustomerToolAccess([]);
                    setAvailableTools([]);
                    setSelectedTools(new Set());
                    setExpandedToolId(null);
                    setFunctionAccessSummaries({});
                    setSelectedFunctions({});
                    setFunctionAccessLoading(null);
                  }}
                  disabled={toolAccessLoading}
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={handleSaveToolAccess}
                  disabled={toolAccessLoading}
                >
                  {toolAccessLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Änderungen speichern
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Platform Configuration Management Dialog */}
      <Dialog open={!!managingPlatformConfig} onOpenChange={() => {
        setManagingPlatformConfig(null);
        setPlatformConfig(null);
        resetFeatureManagementState();
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Plattform-Konfiguration verwalten</DialogTitle>
            <DialogDescription>
              {managingPlatformConfig ? `Verwalte die Plattform-Konfiguration für ${managingPlatformConfig.name}` : ''}
            </DialogDescription>
          </DialogHeader>

          {platformConfigLoading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-600">Lade Plattform-Konfiguration...</span>
            </div>
          ) : platformConfig ? (
            <div className="space-y-6">
              {/* Configuration Options */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900">Funktionen</h3>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-sm text-gray-700">Chatbots anzeigen</label>
                      <Checkbox
                        checked={platformConfig.showChatbots}
                        onCheckedChange={(checked) => handlePlatformConfigChange('showChatbots', checked as boolean)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <label className="text-sm text-gray-700">Voicebots anzeigen</label>
                      <Checkbox
                        checked={platformConfig.showVoicebots}
                        onCheckedChange={(checked) => handlePlatformConfigChange('showVoicebots', checked as boolean)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <label className="text-sm text-gray-700">Wissensmanagement anzeigen</label>
                      <Checkbox
                        checked={platformConfig.showKnowledge}
                        onCheckedChange={(checked) => handlePlatformConfigChange('showKnowledge', checked as boolean)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <label className="text-sm text-gray-700">Templates anzeigen</label>
                      <Checkbox
                        checked={platformConfig.showTemplates}
                        onCheckedChange={(checked) => handlePlatformConfigChange('showTemplates', checked as boolean)}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900">Erweiterte Funktionen</h3>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-sm text-gray-700">Tools anzeigen</label>
                      <Checkbox
                        checked={platformConfig.showTools}
                        onCheckedChange={(checked) => handlePlatformConfigChange('showTools', checked as boolean)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <label className="text-sm text-gray-700">Test-Szenarien anzeigen</label>
                      <Checkbox
                        checked={platformConfig.showTestScenarios}
                        onCheckedChange={(checked) => handlePlatformConfigChange('showTestScenarios', checked as boolean)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <label className="text-sm text-gray-700">Benutzerverwaltung anzeigen</label>
                      <Checkbox
                        checked={platformConfig.showUserManagement}
                        onCheckedChange={(checked) => handlePlatformConfigChange('showUserManagement', checked as boolean)}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900">Voicebot-Funktionen</h3>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-sm text-gray-700">Voice-Funktionalität anzeigen</label>
                      <Checkbox
                        checked={platformConfig.showVoiceFunctionality}
                        onCheckedChange={(checked) => handlePlatformConfigChange('showVoiceFunctionality', checked as boolean)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <label className="text-sm text-gray-700">Speech-To-Text anzeigen</label>
                      <Checkbox
                        checked={platformConfig.showSpeechToText}
                        onCheckedChange={(checked) => handlePlatformConfigChange('showSpeechToText', checked as boolean)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <label className="text-sm text-gray-700">Timeouts-Konfiguration anzeigen</label>
                      <Checkbox
                        checked={platformConfig.showTimeoutsConfig}
                        onCheckedChange={(checked) => handlePlatformConfigChange('showTimeoutsConfig', checked as boolean)}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900">Chatbot-Funktionen</h3>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-sm text-gray-700">Feedback-Funktionalität anzeigen</label>
                      <Checkbox
                        checked={platformConfig.showFeedbackFunctionality}
                        onCheckedChange={(checked) => handlePlatformConfigChange('showFeedbackFunctionality', checked as boolean)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <label className="text-sm text-gray-700">API-Keys-Verwaltung anzeigen</label>
                      <Checkbox
                        checked={platformConfig.showApiKeysManagement}
                        onCheckedChange={(checked) => handlePlatformConfigChange('showApiKeysManagement', checked as boolean)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Data Anonymization Section — automatische Anonymisierung */}
              <div className="space-y-3 border-t pt-4">
                <h3 className="text-sm font-semibold text-gray-900">Datenschutz &amp; Anonymisierung</h3>
                <p className="text-xs text-gray-500">
                  Ein täglicher Job ersetzt Rufnummer und Transkript abgelaufener Konversationen durch Platzhalter.
                  Reporting-Daten (Dauer, Kosten, Tokens, Labels) bleiben erhalten.
                </p>
                <div className="flex items-center justify-between">
                  <label className="text-sm text-gray-700" htmlFor="dataAnonymizationEnabled">
                    Automatische Anonymisierung aktiv
                  </label>
                  <Checkbox
                    id="dataAnonymizationEnabled"
                    checked={platformConfig.dataAnonymizationEnabled}
                    onCheckedChange={(checked) => handlePlatformConfigChange('dataAnonymizationEnabled', checked as boolean)}
                  />
                </div>
                <div className="flex items-center justify-between gap-4">
                  <label className="text-sm text-gray-700" htmlFor="dataRetentionDays">
                    Aufbewahrungsfrist (Tage ab Anrufende)
                  </label>
                  <Input
                    id="dataRetentionDays"
                    type="number"
                    min={1}
                    max={3650}
                    className="w-28"
                    value={retentionDaysInput}
                    disabled={!platformConfig.dataAnonymizationEnabled}
                    onChange={(e) => { setRetentionDaysInput(e.target.value); }}
                  />
                </div>
              </div>

              {/* Feature-Flags Section (CA-380) — Direkt-Grants pro Customer */}
              <div className="space-y-3 border-t pt-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-600" />
                    Features (Direkt-Grants)
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Features, die diesem Customer zusätzlich zu den Features seines Plans
                    freigeschaltet werden. Optional mit Ablaufdatum und Notiz.
                  </p>
                </div>

                {availableFeatures.filter(f => f.isActive).length === 0 ? (
                  <p className="rounded-md border border-dashed py-6 text-center text-sm text-muted-foreground">
                    Es sind noch keine aktiven Features im Katalog.
                  </p>
                ) : (
                  <div className="max-h-[360px] space-y-2 overflow-y-auto rounded-md border p-2">
                    {availableFeatures
                      .filter(f => f.isActive)
                      .map(feature => {
                        const isSelected = selectedFeatures.has(feature.id);
                        const grant = customerFeatureGrants[feature.id];
                        return (
                          <div
                            key={feature.id}
                            className="space-y-2 rounded-md border bg-white p-2.5"
                          >
                            <div className="flex items-start space-x-2">
                              <Checkbox
                                id={`pc-feature-${feature.id}`}
                                checked={isSelected}
                                onCheckedChange={c =>
                                  toggleFeatureSelection(feature.id, c === true)
                                }
                                className="mt-0.5"
                              />
                              <label
                                htmlFor={`pc-feature-${feature.id}`}
                                className="flex-1 cursor-pointer text-sm leading-tight"
                              >
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{feature.displayName}</span>
                                  {grant && initialFeatureSelection.has(feature.id) && (
                                    <Badge variant="outline" className="text-xs">
                                      Aktiv
                                    </Badge>
                                  )}
                                </div>
                                <div className="font-mono text-xs text-muted-foreground">
                                  {feature.name}
                                </div>
                                {feature.description && (
                                  <div className="text-xs text-muted-foreground">
                                    {feature.description}
                                  </div>
                                )}
                              </label>
                            </div>

                            {isSelected && (
                              <div className="ml-6 grid grid-cols-1 gap-2 sm:grid-cols-2">
                                <div>
                                  <label className="mb-1 block text-xs text-gray-600">
                                    Ablaufdatum (optional)
                                  </label>
                                  <Input
                                    type="date"
                                    value={featureExpiryDrafts[feature.id] ?? ''}
                                    onChange={e =>
                                      setFeatureExpiryDrafts(prev => ({
                                        ...prev,
                                        [feature.id]: e.target.value,
                                      }))
                                    }
                                    disabled={platformConfigLoading}
                                    className="h-8 text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="mb-1 block text-xs text-gray-600">
                                    Notiz (optional)
                                  </label>
                                  <Textarea
                                    value={featureNotesDrafts[feature.id] ?? ''}
                                    onChange={e =>
                                      setFeatureNotesDrafts(prev => ({
                                        ...prev,
                                        [feature.id]: e.target.value,
                                      }))
                                    }
                                    placeholder="z.B. Ticket-Referenz"
                                    disabled={platformConfigLoading}
                                    rows={1}
                                    className="text-sm"
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>

              {/* Footer Actions */}
              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setManagingPlatformConfig(null);
                    setPlatformConfig(null);
                    resetFeatureManagementState();
                  }}
                  disabled={platformConfigLoading}
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={handleSavePlatformConfig}
                  disabled={platformConfigLoading}
                >
                  {platformConfigLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Konfiguration speichern
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center p-8">
              <AlertCircle className="w-8 h-8 text-red-400" />
              <span className="ml-2 text-red-600">Fehler beim Laden der Plattform-Konfiguration</span>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* TTS Provider Access Management Dialog */}
      <Dialog
        open={!!managingTtsAccess}
        onOpenChange={() => {
          setManagingTtsAccess(null);
          setCustomerTtsProviders([]);
          setAvailableTtsProviders([]);
          setSelectedTtsProviders(new Set());
        }}
      >
        <DialogContent className="max-w-2xl sm:max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center space-x-2">
              <Volume2 className="w-5 h-5 text-indigo-600" />
              <span>TTS Provider verwalten - {managingTtsAccess?.name}</span>
            </DialogTitle>
            <DialogDescription>
              Verwalten Sie die Zugriffe auf Text-to-Speech Provider für diesen Kunden.
              Ausgewählte Provider sind für die Voice Agents des Kunden verfügbar.
            </DialogDescription>
          </DialogHeader>

          {ttsAccessLoading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span>Lade TTS Provider-Zugriffe...</span>
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col">
              {/* Scrollable body — only this area scrolls, the footer stays pinned */}
              <div className="min-h-0 flex-1 space-y-6 overflow-y-auto pr-1">
              {/* Action Buttons */}
              <div className="flex items-center justify-between border-b pb-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700">
                    {selectedTtsProviders.size} von {availableTtsProviders.length} Providern ausgewählt
                  </h4>
                  <p className="text-xs text-gray-500 mt-1">
                    Wählen Sie die TTS Provider aus, die dieser Kunde verwenden darf
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedTtsProviders(new Set())}
                    disabled={selectedTtsProviders.size === 0}
                  >
                    Alle abwählen
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedTtsProviders(new Set(availableTtsProviders.map(p => p.id)))}
                    disabled={selectedTtsProviders.size === availableTtsProviders.length}
                  >
                    Alle auswählen
                  </Button>
                </div>
              </div>

              {/* TTS providers grouped by provider type — compact, scannable table per type */}
              {(() => {
                // Group the flat provider list by providerType so each type gets its own table block
                const groups = new Map<string, TextToSpeechProvider[]>();
                for (const provider of availableTtsProviders) {
                  const existing = groups.get(provider.providerType);
                  if (existing) {
                    existing.push(provider);
                  } else {
                    groups.set(provider.providerType, [provider]);
                  }
                }
                const sortedGroups = [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0], 'de'));

                if (sortedGroups.length === 0) {
                  return (
                    <div className="py-8 text-center text-sm text-gray-500">
                      Keine TTS Provider verfügbar
                    </div>
                  );
                }

                return (
                  <div className="space-y-6">
                    {sortedGroups.map(([providerType, groupProviders]) => {
                      const groupIds = groupProviders.map(p => p.id);
                      const selectedInGroup = groupIds.filter(id => selectedTtsProviders.has(id)).length;
                      const allSelected = selectedInGroup === groupIds.length;

                      return (
                        <div key={providerType} className="overflow-hidden rounded-lg border border-gray-200">
                          {/* Provider-type group header with select-all for the whole type */}
                          <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-2">
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                checked={allSelected}
                                onCheckedChange={(checked) => {
                                  const newSelected = new Set(selectedTtsProviders);
                                  if (checked) {
                                    groupIds.forEach(id => newSelected.add(id));
                                  } else {
                                    groupIds.forEach(id => newSelected.delete(id));
                                  }
                                  setSelectedTtsProviders(newSelected);
                                }}
                              />
                              <span className="text-sm font-semibold capitalize text-gray-800">{providerType}</span>
                              <Badge variant="outline" className="text-xs">{groupProviders.length}</Badge>
                            </div>
                            <span className="text-xs text-gray-500">{selectedInGroup} ausgewählt</span>
                          </div>

                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-10"></TableHead>
                                <TableHead>Provider</TableHead>
                                <TableHead className="w-28">Zugriff</TableHead>
                                <TableHead className="w-16 text-center">Details</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {groupProviders.map((provider) => {
                                const isSelected = selectedTtsProviders.has(provider.id);
                                const hasAccess = customerTtsProviders.find(tp => tp.providerId === provider.id && tp.isActive);
                                const toggle = () => {
                                  const newSelected = new Set(selectedTtsProviders);
                                  if (isSelected) {
                                    newSelected.delete(provider.id);
                                  } else {
                                    newSelected.add(provider.id);
                                  }
                                  setSelectedTtsProviders(newSelected);
                                };

                                // Resolve a friendly base-URL hostname for the details popover
                                let baseUrlLabel = 'Standard';
                                if (provider.baseUrl) {
                                  try {
                                    baseUrlLabel = new URL(provider.baseUrl).hostname;
                                  } catch {
                                    baseUrlLabel = provider.baseUrl;
                                  }
                                }

                                return (
                                  <TableRow
                                    key={provider.id}
                                    className={`cursor-pointer ${isSelected ? 'bg-indigo-50' : ''}`}
                                    onClick={toggle}
                                  >
                                    <TableCell onClick={(e) => { e.stopPropagation(); }}>
                                      <Checkbox checked={isSelected} onCheckedChange={toggle} />
                                    </TableCell>
                                    <TableCell>
                                      <div className="font-medium text-gray-900">{provider.displayName}</div>
                                      <div className="text-xs text-gray-500">{provider.name}</div>
                                    </TableCell>
                                    <TableCell>
                                      {hasAccess ? (
                                        <Badge variant="outline" className="border-green-200 text-green-700">
                                          <CheckCircle className="mr-1 h-3 w-3" /> Aktiv
                                        </Badge>
                                      ) : (
                                        <span className="text-xs text-gray-400">—</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-center" onClick={(e) => { e.stopPropagation(); }}>
                                      <Popover>
                                        <PopoverTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 w-7 p-0 text-gray-400 hover:text-gray-700"
                                            title="Details anzeigen"
                                          >
                                            <HelpCircle className="h-4 w-4" />
                                          </Button>
                                        </PopoverTrigger>
                                        <PopoverContent align="end" className="w-72 text-xs">
                                          <div className="space-y-3">
                                            <div className="space-y-0.5 text-gray-600">
                                              <div className="flex justify-between">
                                                <span>Status</span>
                                                <Badge variant={provider.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                                                  {provider.status === 'active' ? 'Aktiv' : 'Inaktiv'}
                                                </Badge>
                                              </div>
                                              <div className="flex justify-between">
                                                <span>API Key</span>
                                                <span>{provider.hasDefaultApiKey ? 'Standard' : 'Custom'}</span>
                                              </div>
                                              <div className="flex justify-between">
                                                <span>Stimmen</span>
                                                <span>{provider.voices.length}</span>
                                              </div>
                                              <div className="flex justify-between gap-2">
                                                <span>Base URL</span>
                                                <span className="truncate" title={provider.baseUrl}>{baseUrlLabel}</span>
                                              </div>
                                            </div>
                                            {provider.voices.length > 0 && (
                                              <div>
                                                <div className="mb-1 font-medium text-gray-700">Beispiel-Stimmen</div>
                                                <div className="flex flex-wrap gap-1">
                                                  {provider.voices.slice(0, 3).map((voice, index) => (
                                                    <Badge key={index} variant="outline" className="px-1 py-0 text-xs">
                                                      {voice.name}
                                                    </Badge>
                                                  ))}
                                                  {provider.voices.length > 3 && (
                                                    <Badge variant="outline" className="px-1 py-0 text-xs">
                                                      +{provider.voices.length - 3}
                                                    </Badge>
                                                  )}
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </PopoverContent>
                                      </Popover>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              </div>
              {/* Footer Actions — pinned to the bottom, outside the scroll area */}
              <div className="flex shrink-0 justify-end space-x-2 border-t pt-4 mt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setManagingTtsAccess(null);
                    setCustomerTtsProviders([]);
                    setAvailableTtsProviders([]);
                    setSelectedTtsProviders(new Set());
                  }}
                  disabled={ttsAccessLoading}
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={handleSaveTtsAccess}
                  disabled={ttsAccessLoading}
                >
                  {ttsAccessLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Änderungen speichern
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* STT Provider Access Management Dialog */}
      <Dialog
        open={!!managingSttAccess}
        onOpenChange={() => {
          setManagingSttAccess(null);
          setCustomerSttProviders([]);
          setAvailableSttProviders([]);
          setSelectedSttProviders(new Set());
        }}
      >
        <DialogContent className="max-w-2xl sm:max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center space-x-2">
              <Mic className="w-5 h-5 text-purple-600" />
              <span>STT Provider verwalten - {managingSttAccess?.name}</span>
            </DialogTitle>
            <DialogDescription>
              Verwalten Sie die Zugriffe auf Speech-to-Text Provider für diesen Kunden.
              Ausgewählte Provider sind für die Voice Agents des Kunden verfügbar.
            </DialogDescription>
          </DialogHeader>

          {sttAccessLoading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span>Lade STT Provider-Zugriffe...</span>
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col">
              {/* Scrollable body — only this area scrolls, the footer stays pinned */}
              <div className="min-h-0 flex-1 space-y-6 overflow-y-auto pr-1">
              {/* Action Buttons */}
              <div className="flex items-center justify-between border-b pb-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-900">
                    {selectedSttProviders.size} von {availableSttProviders.length} Providern ausgewählt
                  </h4>
                  <p className="text-xs text-gray-500 mt-1">
                    Wählen Sie die STT Provider aus, die dieser Kunde verwenden darf
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setSelectedSttProviders(new Set()); }}
                    disabled={selectedSttProviders.size === 0}
                  >
                    Alle abwählen
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setSelectedSttProviders(new Set(availableSttProviders.map(p => p.id))); }}
                    disabled={selectedSttProviders.size === availableSttProviders.length}
                  >
                    Alle auswählen
                  </Button>
                </div>
              </div>

              {/* STT providers grouped by provider type — compact, scannable table per type */}
              {availableSttProviders.length === 0 ? (
                <div className="py-8 text-center">
                  <Mic className="mx-auto mb-4 h-12 w-12 text-gray-300" />
                  <h4 className="mb-2 text-sm font-medium text-gray-900">
                    Keine STT Provider verfügbar
                  </h4>
                  <p className="text-xs text-gray-500">
                    Es wurden noch keine Speech-to-Text Provider konfiguriert.
                  </p>
                </div>
              ) : (() => {
                // Group the flat provider list by providerType so each type gets its own table block
                const groups = new Map<string, SpeechToTextProvider[]>();
                for (const provider of availableSttProviders) {
                  const existing = groups.get(provider.providerType);
                  if (existing) {
                    existing.push(provider);
                  } else {
                    groups.set(provider.providerType, [provider]);
                  }
                }
                const sortedGroups = [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0], 'de'));

                return (
                  <div className="space-y-6">
                    {sortedGroups.map(([providerType, groupProviders]) => {
                      const groupIds = groupProviders.map(p => p.id);
                      const selectedInGroup = groupIds.filter(id => selectedSttProviders.has(id)).length;
                      const allSelected = selectedInGroup === groupIds.length;

                      return (
                        <div key={providerType} className="overflow-hidden rounded-lg border border-gray-200">
                          {/* Provider-type group header with select-all for the whole type */}
                          <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-2">
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                checked={allSelected}
                                onCheckedChange={(checked) => {
                                  const newSelected = new Set(selectedSttProviders);
                                  if (checked) {
                                    groupIds.forEach(id => newSelected.add(id));
                                  } else {
                                    groupIds.forEach(id => newSelected.delete(id));
                                  }
                                  setSelectedSttProviders(newSelected);
                                }}
                              />
                              <span className="text-sm font-semibold capitalize text-gray-800">{providerType}</span>
                              <Badge variant="outline" className="text-xs">{groupProviders.length}</Badge>
                            </div>
                            <span className="text-xs text-gray-500">{selectedInGroup} ausgewählt</span>
                          </div>

                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-10"></TableHead>
                                <TableHead>Provider</TableHead>
                                <TableHead className="w-28">Zugriff</TableHead>
                                <TableHead className="w-16 text-center">Details</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {groupProviders.map((provider) => {
                                const isSelected = selectedSttProviders.has(provider.id);
                                const isCurrentlyAssigned = customerSttProviders.some(
                                  cp => cp.providerId === provider.id && cp.isActive
                                );
                                const toggle = () => {
                                  const newSelected = new Set(selectedSttProviders);
                                  if (isSelected) {
                                    newSelected.delete(provider.id);
                                  } else {
                                    newSelected.add(provider.id);
                                  }
                                  setSelectedSttProviders(newSelected);
                                };

                                return (
                                  <TableRow
                                    key={provider.id}
                                    className={`cursor-pointer ${isSelected ? 'bg-purple-50' : ''}`}
                                    onClick={toggle}
                                  >
                                    <TableCell onClick={(e) => { e.stopPropagation(); }}>
                                      <Checkbox checked={isSelected} onCheckedChange={toggle} />
                                    </TableCell>
                                    <TableCell>
                                      <div className="font-medium text-gray-900">{provider.displayName}</div>
                                      <div className="text-xs text-gray-500">{provider.name}</div>
                                    </TableCell>
                                    <TableCell>
                                      {isCurrentlyAssigned ? (
                                        <Badge variant="outline" className="border-green-200 text-green-700">
                                          <CheckCircle className="mr-1 h-3 w-3" /> Aktiv
                                        </Badge>
                                      ) : (
                                        <span className="text-xs text-gray-400">—</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-center" onClick={(e) => { e.stopPropagation(); }}>
                                      <Popover>
                                        <PopoverTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 w-7 p-0 text-gray-400 hover:text-gray-700"
                                            title="Details anzeigen"
                                          >
                                            <HelpCircle className="h-4 w-4" />
                                          </Button>
                                        </PopoverTrigger>
                                        <PopoverContent align="end" className="w-72 text-xs">
                                          <div className="space-y-0.5 text-gray-600">
                                            <div className="flex justify-between">
                                              <span>Status</span>
                                              <Badge variant={provider.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                                                {provider.status === 'active' ? 'Aktiv' : 'Inaktiv'}
                                              </Badge>
                                            </div>
                                            <div className="flex justify-between">
                                              <span>API Key</span>
                                              <span>{provider.apiKeyRequired ? 'Erforderlich' : 'Optional'}</span>
                                            </div>
                                            <div className="flex justify-between">
                                              <span>Modelle</span>
                                              <span>{provider.models.length}</span>
                                            </div>
                                          </div>
                                        </PopoverContent>
                                      </Popover>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              </div>
              {/* Footer Actions — pinned to the bottom, outside the scroll area */}
              <div className="flex shrink-0 justify-end space-x-2 border-t pt-4 mt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setManagingSttAccess(null);
                    setCustomerSttProviders([]);
                    setAvailableSttProviders([]);
                    setSelectedSttProviders(new Set());
                  }}
                  disabled={sttAccessLoading}
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={handleSaveSttAccess}
                  disabled={sttAccessLoading}
                >
                  {sttAccessLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Zugriffe speichern
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Subscription Plan Assignment Dialog */}
      <Dialog
        open={!!managingSubscription}
        onOpenChange={() => {
          setManagingSubscription(null);
          setAvailablePlans([]);
          setSelectedPlanId('');
        }}
      >
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <CreditCard className="w-5 h-5 text-amber-600" />
              <span>Abonnement verwalten - {managingSubscription?.name}</span>
            </DialogTitle>
            <DialogDescription>
              Weisen Sie diesem Kunden einen Abonnement-Plan zu oder ändern Sie den bestehenden Plan.
              {managingSubscription?.subscription?.planName && (
                <span className="flex items-center gap-2 mt-1">
                  <span className="text-amber-600">
                    Aktueller Plan: {managingSubscription.subscription.planName}
                  </span>
                  {managingSubscription.subscription.status === 'suspended' ? (
                    <Badge variant="secondary" className="bg-red-100 text-red-800">Pausiert</Badge>
                  ) : (
                    <Badge variant="secondary" className="bg-green-100 text-green-800">{managingSubscription.subscription.status}</Badge>
                  )}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {subscriptionLoading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span>Lade Abonnement-Pläne...</span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Plan Selection */}
              <div className="space-y-2">
                <Label htmlFor="plan-select" className="text-sm font-medium">
                  Neuen Plan auswählen
                </Label>
                <Select
                  value={selectedPlanId}
                  onValueChange={setSelectedPlanId}
                >
                  <SelectTrigger id="plan-select" className="w-full">
                    <SelectValue placeholder="Plan auswählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availablePlans.map((plan) => (
                      <SelectItem key={plan.id} value={plan.id}>
                        <div className="flex items-center justify-between w-full">
                          <span>{plan.name}</span>
                          <span className="text-gray-500 ml-2">
                            {(plan.priceInCents / 100).toFixed(2)} {plan.currency}/{plan.billingPeriod === 'monthly' ? 'Monat' : plan.billingPeriod === 'yearly' ? 'Jahr' : 'Woche'}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Plan Comparison: Two Cards Side by Side */}
              {(() => {
                const newPlan = availablePlans.find(p => p.id === selectedPlanId);
                const curr = currentSubscription;
                const formatCents = (v: number, currency: string) => `${(v / 100).toFixed(2)} ${currency}`;
                const formatLimit = (v: number | null | undefined) => v == null ? '∞' : String(v);

                type ConditionRow = { label: string; currentValue: string; newValue: string; changed: boolean };

                const buildRows = (): ConditionRow[] => {
                  if (!newPlan) return [];
                  const np = newPlan;
                  const cc = curr?.currency ?? '';
                  return [
                    { label: 'Grundgebühr', currentValue: curr ? formatCents(curr.baseFeeInCents, cc) : '–', newValue: formatCents(np.priceInCents, np.currency), changed: curr?.baseFeeInCents !== np.priceInCents },
                    { label: 'Pro Minute', currentValue: curr ? formatCents(curr.pricePerMinuteInCents, cc) : '–', newValue: formatCents(np.pricePerMinuteInCents, np.currency), changed: curr?.pricePerMinuteInCents !== np.pricePerMinuteInCents },
                    { label: 'Pro Chat', currentValue: curr ? formatCents(curr.pricePerChatInCents, cc) : '–', newValue: formatCents(np.pricePerChatInCents, np.currency), changed: curr?.pricePerChatInCents !== np.pricePerChatInCents },
                    { label: 'Pro Weiterleitung', currentValue: curr ? formatCents(curr.pricePerExternalForwardingInCents, cc) : '–', newValue: formatCents(np.pricePerExternalForwardingInCents, np.currency), changed: curr?.pricePerExternalForwardingInCents !== np.pricePerExternalForwardingInCents },
                    { label: 'Tel. Setup-Gebühr', currentValue: curr ? formatCents(curr.phoneSetupFeeInCents, cc) : '–', newValue: formatCents(np.phoneSetupFeeInCents, np.currency), changed: curr?.phoneSetupFeeInCents !== np.phoneSetupFeeInCents },
                    { label: 'Tel. Monatsgebühr', currentValue: curr ? formatCents(curr.phoneMonthlyFeeInCents, cc) : '–', newValue: formatCents(np.phoneMonthlyFeeInCents, np.currency), changed: curr?.phoneMonthlyFeeInCents !== np.phoneMonthlyFeeInCents },
                    { label: 'Inkl. Telefonnummern', currentValue: curr ? formatLimit(curr.includedPhoneNumbers) : '–', newValue: formatLimit(np.includedPhoneNumbers), changed: curr?.includedPhoneNumbers !== (np.includedPhoneNumbers ?? 0) },
                    { label: 'Max. Telefonnummern', currentValue: curr ? formatLimit(curr.maxPhoneNumbers) : '–', newValue: formatLimit(np.maxPhoneNumbers), changed: curr?.maxPhoneNumbers !== np.maxPhoneNumbers },
                    { label: 'Max. Chatbots', currentValue: curr ? formatLimit(curr.maxChatbots) : '–', newValue: formatLimit(np.maxChatbots), changed: curr?.maxChatbots !== np.maxChatbots },
                    { label: 'Max. Voicebots', currentValue: curr ? formatLimit(curr.maxVoicebots) : '–', newValue: formatLimit(np.maxVoicebots), changed: curr?.maxVoicebots !== np.maxVoicebots },
                    { label: 'Max. Anrufminuten', currentValue: curr ? formatLimit(curr.maxCallMinutes) : '–', newValue: formatLimit(np.maxCallMinutes), changed: curr?.maxCallMinutes !== np.maxCallMinutes },
                    { label: 'Max. Konversationen', currentValue: curr ? formatLimit(curr.maxConversations) : '–', newValue: formatLimit(np.maxConversations), changed: curr?.maxConversations !== np.maxConversations },
                    { label: 'Max. Testruns', currentValue: curr ? formatLimit(curr.maxTestRuns) : '–', newValue: formatLimit(np.maxTestRuns), changed: curr?.maxTestRuns !== np.maxTestRuns },
                    { label: 'Max. Weiterleitungen', currentValue: curr ? formatLimit(curr.maxExternalForwardings) : '–', newValue: formatLimit(np.maxExternalForwardings), changed: curr?.maxExternalForwardings !== np.maxExternalForwardings },
                  ];
                };

                if (!selectedPlanId || !newPlan) return null;

                const rows = buildRows();

                return (
                  <div className="flex items-stretch gap-4">
                    {/* Left: Current Plan */}
                    <Card className="flex-1">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2">
                          {curr?.plan ? curr.plan.name : 'Kein Plan'}
                          {curr?.plan && <Badge variant="secondary" className="bg-gray-100 text-gray-600">Aktuell</Badge>}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="space-y-0">
                          {rows.map((row, i) => (
                            <div
                              key={row.label}
                              className={`flex justify-between py-1.5 px-2 text-sm rounded ${row.changed ? 'bg-yellow-100' : i % 2 === 0 ? '' : 'bg-gray-50'}`}
                            >
                              <span className="text-gray-500">{row.label}</span>
                              <span className="font-medium text-gray-900">{row.currentValue}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                    {/* Arrow */}
                    <div className="flex items-center">
                      <ArrowRight className="w-6 h-6 text-amber-500" />
                    </div>
                    {/* Right: New Plan */}
                    <Card className="flex-1">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2">
                          {newPlan.name}
                          <Badge variant="secondary" className="bg-amber-100 text-amber-700">Neu</Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="space-y-0">
                          {rows.map((row, i) => (
                            <div
                              key={row.label}
                              className={`flex justify-between py-1.5 px-2 text-sm rounded ${row.changed ? 'bg-yellow-100' : i % 2 === 0 ? '' : 'bg-gray-50'}`}
                            >
                              <span className="text-gray-500">{row.label}</span>
                              <span className={`font-medium ${row.changed ? 'text-amber-700 font-semibold' : 'text-gray-900'}`}>
                                {row.newValue}
                              </span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                );
              })()}

              {availablePlans.length === 0 && (
                <div className="text-center py-8">
                  <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                    Keine Pläne verfügbar
                  </h4>
                  <p className="text-xs text-gray-500">
                    Es wurden noch keine aktiven Abonnement-Pläne erstellt.
                  </p>
                </div>
              )}

              {/* Account Status Section */}
              {managingSubscription?.subscription && (
                <div className="border-t pt-4 space-y-3">
                  <Label className="text-sm font-medium">Kontostatus</Label>
                  {managingSubscription.subscription.status === 'suspended' ? (
                    <div className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <PauseCircle className="w-5 h-5 text-red-600" />
                        <span className="text-sm text-red-800 font-medium">Konto ist pausiert</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-green-300 text-green-700 hover:bg-green-50"
                          onClick={() => setResumeDialogOpen(true)}
                        >
                          <PlayCircle className="w-4 h-4 mr-1" />
                          Reaktivieren
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-red-400 text-red-700 hover:bg-red-100"
                          onClick={() => setDeleteDialogOpen(true)}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Konto löschen
                        </Button>
                      </div>
                    </div>
                  ) : managingSubscription.subscription.status === 'active' ? (
                    <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="text-sm text-gray-700">Konto ist aktiv</span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-red-300 text-red-700 hover:bg-red-50"
                        onClick={() => setSuspendDialogOpen(true)}
                      >
                        <PauseCircle className="w-4 h-4 mr-1" />
                        Konto pausieren
                      </Button>
                    </div>
                  ) : null}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-2 border-t pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setManagingSubscription(null);
                    setAvailablePlans([]);
                    setSelectedPlanId('');
                    setCurrentSubscription(null);
                  }}
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={handleSaveSubscription}
                  disabled={subscriptionLoading || !selectedPlanId}
                >
                  {subscriptionLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  {managingSubscription?.subscription?.planName ? 'Plan ändern' : 'Plan zuweisen'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Suspend Confirmation Dialog */}
      <AlertDialog open={suspendDialogOpen} onOpenChange={setSuspendDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Konto pausieren</AlertDialogTitle>
            <AlertDialogDescription>
              Sind Sie sicher, dass Sie das Konto von <strong>{managingSubscription?.name}</strong> pausieren
              möchten? Alle Agents werden deaktiviert und sind nicht mehr erreichbar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Label htmlFor="suspend-reason" className="text-sm font-medium">
              Grund (optional)
            </Label>
            <Textarea
              id="suspend-reason"
              placeholder="z.B. Unbezahlte Rechnung"
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              className="mt-1"
              rows={2}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={suspendLoading} onClick={() => setSuspendReason('')}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={suspendLoading}
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={handleSuspendCustomer}
            >
              {suspendLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Konto pausieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Resume Confirmation Dialog */}
      <AlertDialog open={resumeDialogOpen} onOpenChange={setResumeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Konto reaktivieren</AlertDialogTitle>
            <AlertDialogDescription>
              Das Konto von <strong>{managingSubscription?.name}</strong> wird reaktiviert.
              Zuvor pausierte Agents werden wieder aktiviert.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={suspendLoading}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              disabled={suspendLoading}
              style={{ backgroundColor: '#16a34a', color: 'white' }}
              onClick={handleResumeCustomer}
            >
              {suspendLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Konto reaktivieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Customer Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Konto unwiderruflich löschen</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="block mb-2">
                Sind Sie sicher, dass Sie das Konto von <strong>{managingSubscription?.name}</strong> vollständig löschen möchten?
              </span>
              <span className="block text-red-700 font-medium">
                Diese Aktion kann nicht rückgängig gemacht werden. Alle Daten (Agents, Nutzer, Sessions, Abonnement) werden dauerhaft gelöscht.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLoading}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteLoading}
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={handleDeleteCustomer}
            >
              {deleteLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Dauerhaft löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* New Customer Creation Dialog */}
      <Dialog open={showNewCustomerDialog} onOpenChange={setShowNewCustomerDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Neuen Customer erstellen</DialogTitle>
            <DialogDescription>
              Erstellen Sie einen neuen Customer-Account. Der designierte Owner erhält eine Email mit einem Link zum Setzen des Passworts.
            </DialogDescription>
          </DialogHeader>

          {newCustomerLoading && registrationProfiles.length === 0 ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span>Lade Registrierungsprofile...</span>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Company Information */}
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-900">Firmeninformationen</h3>

                <div className="space-y-2">
                  <Label htmlFor="companyName">Firmenname *</Label>
                  <Input
                    id="companyName"
                    placeholder="z.B. Acme Corporation"
                    value={newCustomerData.companyName}
                    onChange={(e) => setNewCustomerData(prev => ({ ...prev, companyName: e.target.value }))}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Beschreibung (optional)</Label>
                  <Input
                    id="description"
                    placeholder="z.B. Enterprise Kunde - Automotive"
                    value={newCustomerData.description}
                    onChange={(e) => setNewCustomerData(prev => ({ ...prev, description: e.target.value }))}
                  />
                </div>
              </div>

              {/* Owner Information */}
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-900">Owner-Informationen</h3>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="ownerName">Name *</Label>
                    <Input
                      id="ownerName"
                      placeholder="z.B. Max Mustermann"
                      value={newCustomerData.ownerName}
                      onChange={(e) => setNewCustomerData(prev => ({ ...prev, ownerName: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ownerEmail">Email *</Label>
                    <Input
                      id="ownerEmail"
                      type="email"
                      placeholder="z.B. owner@acme.com"
                      value={newCustomerData.ownerEmail}
                      onChange={(e) => setNewCustomerData(prev => ({ ...prev, ownerEmail: e.target.value }))}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ownerPhoneNumber">Telefonnummer (optional)</Label>
                  <Input
                    id="ownerPhoneNumber"
                    placeholder="z.B. +49 123 456789"
                    value={newCustomerData.ownerPhoneNumber}
                    onChange={(e) => setNewCustomerData(prev => ({ ...prev, ownerPhoneNumber: e.target.value }))}
                  />
                </div>
              </div>

              {/* Registration Profile Selection */}
              <div className="space-y-2">
                <Label htmlFor="registrationProfile">Registrierungsprofil *</Label>
                <Select
                  value={newCustomerData.registrationProfileId}
                  onValueChange={(value) => setNewCustomerData(prev => ({ ...prev, registrationProfileId: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Wählen Sie ein Registrierungsprofil" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.isArray(registrationProfiles) && registrationProfiles.map((profile) => (
                      <SelectItem key={profile.id} value={profile.id}>
                        {profile.name} {profile.isDefault && '(Standard)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {newCustomerData.registrationProfileId && Array.isArray(registrationProfiles) && (
                  <p className="text-xs text-gray-500">
                    {registrationProfiles.find(p => p.id === newCustomerData.registrationProfileId)?.description || ''}
                  </p>
                )}
              </div>

              {/* Creation Method Selection */}
              <div className="space-y-2">
                <Label htmlFor="creationMethod">Erstellungsverfahren *</Label>
                <Select
                  value={creationMethod}
                  onValueChange={(value) => setCreationMethod(value as 'direct' | 'request' | '')}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Wählen Sie ein Erstellungsverfahren" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="direct">
                      Direkt erstellen und Passwort versenden
                    </SelectItem>
                    <SelectItem value="request">
                      Registierungsanfrage erstellen
                    </SelectItem>
                  </SelectContent>
                </Select>
                {creationMethod && (
                  <p className="text-xs text-gray-500">
                    {creationMethod === 'direct'
                      ? 'Customer wird sofort erstellt und erhält eine Email mit dem generierten Passwort.'
                      : 'Erstellt eine Registierungsanfrage. Der Owner verifiziert selbst per Email-Link.'}
                  </p>
                )}
              </div>

              {/* Info Box - only shown when creation method is selected */}
              {creationMethod && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div className="text-sm text-blue-900">
                      <p className="font-medium mb-1">
                        {creationMethod === 'direct' ? 'Direkte Erstellung:' : 'Nächste Schritte:'}
                      </p>
                      {creationMethod === 'direct' ? (
                        <ol className="list-decimal list-inside space-y-1 text-xs">
                          <li>Customer-Account wird sofort erstellt</li>
                          <li>Passwort wird automatisch generiert</li>
                          <li>Owner erhält eine Email mit dem Passwort</li>
                          <li>Das Registrierungsprofil wird automatisch angewendet</li>
                        </ol>
                      ) : (
                        <ol className="list-decimal list-inside space-y-1 text-xs">
                          <li>Der Owner erhält eine Email mit einem Verifizierungs-Link</li>
                          <li>Der Link ist 24 Stunden gültig</li>
                          <li>Nach Klick auf den Link setzt der Owner selbst ein Passwort</li>
                          <li>Das Registrierungsprofil wird automatisch angewendet</li>
                        </ol>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowNewCustomerDialog(false);
                    setNewCustomerData({
                      companyName: '',
                      description: '',
                      ownerEmail: '',
                      ownerName: '',
                      ownerPhoneNumber: '',
                      registrationProfileId: '',
                    });
                  }}
                  disabled={newCustomerLoading}
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={handleCreateCustomer}
                  disabled={newCustomerLoading || !newCustomerData.companyName || !newCustomerData.ownerEmail || !newCustomerData.ownerName || !newCustomerData.registrationProfileId}
                >
                  {newCustomerLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Customer erstellen
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CustomerCostOverviewDialog
        customer={costOverviewCustomer}
        onClose={() => setCostOverviewCustomer(null)}
      />
    </div>
  );
}
